/**
 * Agentic v2 API client.
 *
 * Thin wrappers around `/api/v1/agentic-v2/*` endpoints.
 */

import axios from "axios";

import { apiRequest, browserApiUrl } from "../client";
import type { FeToolCall } from "../in-app-agent";
import type {
  AddArtifactCommentInput,
  AgentSummary,
  CreateFactfindResult,
  FactfindDefaultsRule,
  FactfindPortalEventKind,
  FactfindRead,
  OutstandingFactfind,
  ApplyTaskActionRequest,
  BatchApplyTaskActionsRequest,
  BatchApplyTaskActionsResponse,
  AssignTaskHumanRequest,
  AssignTaskHumanResponse,
  Artifact,
  ArtifactComment,
  ArtifactCommentStatus,
  ArtifactChatMessage,
  AskAniInput,
  AskAniIntent,
  ClientsStageSummaryParams,
  FaClientsStageSummary,
  SuggestArtifactCommentsInput,
  SuggestArtifactCommentsResult,
  ArtifactDocumentJoined,
  ArtifactDocumentVersion,
  ClientInsight,
  ClientMemory,
  CreateLoaInput,
  DocumentTemplate,
  FormSchema,
  ClientTimelineEntry,
  CreateClientMemoryRequest,
  CreateCaseCheckMissionRequest,
  CreateCaseCheckMissionResponse,
  CreateDisclosureMissionRequest,
  CreateDisclosureMissionResponse,
  CreateMissionRequest,
  CreateMissionResponse,
  CreateTaskRequest,
  CreateTaskResponse,
  CrmField,
  CodexMissionTemplateDetail,
  DataGatheringOpsBucket,
  DataGatheringOpsSummary,
  DgCheatSheetsOpsList,
  DgFailedDocumentsOpsList,
  DgMonitoringActionResult,
  DgPlannrResyncResult,
  GetPulseDashboardParams,
  GetPulseViewParams,
  PulseDashboardCatalogItem,
  PulseDashboardResponse,
  PulseJourneyParitySnapshotResponse,
  PulseMiBriefing,
  PulseMiDashboard,
  PulseMiDashboardSummary,
  PulsePackAvailability,
  PulseDerivedRefreshParams,
  PulseDerivedRefreshResult,
  PulseViewAssignee,
  PulseViewCatalogItem,
  PulseViewResponse,
  FaClientDataGathering,
  FaClientDetail,
  FaClientListItem,
  FaClientSummary,
  FaClientUpdate,
  FaLeadListItem,
  FaLeadStatuses,
  ListArtifactsParams,
  ListFaClientsParams,
  ListLeadsParams,
  ListMissionsParams,
  ListTaskCatalogueParams,
  ListToolsParams,
  MissionControlFeedStats,
  MissionControlFeedPage,
  MissionControlBucketsResponse,
  Page,
  MissionDetail,
  MissionMatchCandidate,
  MissionStepLogOut,
  MissionSummary,
  MissionTimelineOut,
  TaskTimelineOut,
  TimelineScopeSearchOut,
  MissionTemplateSummary,
  PostMessageRequest,
  UploadDocumentResponse,
  TableSchema,
  ChecklistSchema,
  ChecklistSchemaSection,
  ChecklistItemStatus,
  TaskActionItem,
  TaskBucket,
  TaskCatalogueItem,
  TaskDetail,
  ThreadInteraction,
  ToolSummary,
  ThreadMessage,
  ThreadView,
  PlannrFormSubmissionResponse,
  PlannrFormsListResponse,
  PlannrWebhookRun,
  TriggerEventRow,
  TriggerPendingAction,
  TriggerRule,
  VisibilityScope,
  SurfaceListItem,
  SurfaceSpec,
  SurfaceSnapshot,
  GetSurfaceSnapshotParams,
  HomeLogoVariant,
  SurfaceLogoUploadResult,
  StandupBriefing,
  GetStandupBriefingParams,
} from "./types";

const BASE = "/agentic-v2";

export type { Page, MissionControlFeedStats } from "./types";
export { fetchAllPages } from "./pagination";

/**
 * List missions for the current user's company.
 *
 * @param params - Optional filter params (template, status, attention, search)
 * @returns Array of mission summaries (most recently updated first)
 */
export async function listMissions(
  params: ListMissionsParams = {}
): Promise<Page<MissionSummary>> {
  const search: Record<string, string | number | boolean> = {};
  if (params.template_id) search.template_id = params.template_id;
  if (params.status) search.status = params.status;
  if (params.needs_attention !== undefined)
    search.needs_attention = params.needs_attention;
  if (params.assigned) search.assigned = params.assigned;
  if (params.visibility) search.visibility = params.visibility;
  if (params.as_user_id) search.as_user_id = params.as_user_id;
  if (params.fa_client_id) search.fa_client_id = params.fa_client_id;
  if (params.q) search.q = params.q;
  if (params.limit !== undefined) search.limit = params.limit;
  if (params.skip !== undefined) search.skip = params.skip;
  if (params.cursor) search.cursor = params.cursor;
  if (params.created_since) search.created_since = params.created_since;

  return apiRequest<Page<MissionSummary>>({
    method: "GET",
    url: `${BASE}/missions`,
    params: search,
  });
}

/**
 * Fetch a single mission with its tasks and stage progression.
 *
 * @param missionId - Mission ObjectId
 */
export async function getMission(missionId: string): Promise<MissionDetail> {
  return apiRequest<MissionDetail>({
    method: "GET",
    url: `${BASE}/missions/${missionId}`,
  });
}

/** Kit step audit log for a mission (queued + completed graph steps). */
export async function getMissionStepLog(
  missionId: string,
): Promise<MissionStepLogOut> {
  return apiRequest<MissionStepLogOut>({
    method: "GET",
    url: `${BASE}/mission-control/missions/${encodeURIComponent(missionId)}/step-log`,
  });
}

/** Chronological rollup for the Guardians mission timeline view. */
export async function getMissionTimeline(
  missionId: string
): Promise<MissionTimelineOut> {
  return apiRequest<MissionTimelineOut>({
    method: "GET",
    url: `${BASE}/missions/${encodeURIComponent(missionId)}/timeline`,
  });
}

/** Chronological rollup for a standalone task timeline view. */
export async function getTaskTimeline(taskId: string): Promise<TaskTimelineOut> {
  return apiRequest<TaskTimelineOut>({
    method: "GET",
    url: `${BASE}/tasks/${encodeURIComponent(taskId)}/timeline`,
  });
}

export async function searchTimelineScope(
  q: string,
  options: { kinds?: Array<"mission" | "task">; limit?: number } = {}
): Promise<TimelineScopeSearchOut> {
  const params: Record<string, string | number> = { q };
  if (options.kinds?.length) params.kinds = options.kinds.join(",");
  if (options.limit !== undefined) params.limit = options.limit;
  return apiRequest<TimelineScopeSearchOut>({
    method: "GET",
    url: `${BASE}/timeline/search`,
    params,
  });
}

/**
 * Create a new mission from a template + entity.
 *
 * @param body - template_id + fa_client_id
 * @returns IDs of the created mission and its first queued task
 */
export async function createMission(
  body: CreateMissionRequest
): Promise<CreateMissionResponse> {
  return apiRequest<CreateMissionResponse>({
    method: "POST",
    url: `${BASE}/missions`,
    data: body,
  });
}

/** Launch a compliance-case mission (no fa_client). */
export async function createCaseCheckMission(
  body: CreateCaseCheckMissionRequest = {},
): Promise<CreateCaseCheckMissionResponse> {
  return apiRequest<CreateCaseCheckMissionResponse>({
    method: "POST",
    url: `${BASE}/missions/case-check`,
    data: body,
  });
}

/** Launch a Clara large-position disclosure mission (optionally on an existing case). */
export async function createDisclosureMission(
  body: CreateDisclosureMissionRequest = {},
): Promise<CreateDisclosureMissionResponse> {
  return apiRequest<CreateDisclosureMissionResponse>({
    method: "POST",
    url: `${BASE}/missions/disclosures-mission`,
    data: body,
  });
}

/**
 * List task-catalogue entries for the Task Command Palette.
 *
 * Defaults to manually-invokable entries only; pass `include_internal: true`
 * for admin tooling that needs the full catalogue.
 */
export async function listTaskCatalogue(
  params: ListTaskCatalogueParams = {},
): Promise<TaskCatalogueItem[]> {
  const search: Record<string, string | number | boolean> = {};
  if (params.q) search.q = params.q;
  if (params.entity_type) search.entity_type = params.entity_type;
  if (params.include_internal) search.include_internal = true;
  if (params.limit !== undefined) search.limit = params.limit;
  return apiRequest<TaskCatalogueItem[]>({
    method: "GET",
    url: `${BASE}/task-catalogue`,
    params: search,
  });
}

/**
 * Suggest active missions a manual task could attach to.
 *
 * Returns an empty array when no eligible mission exists; the caller
 * then runs the task standalone.
 */
export async function matchCatalogueEntry(
  catalogueId: string,
  entityId: string,
): Promise<MissionMatchCandidate[]> {
  return apiRequest<MissionMatchCandidate[]>({
    method: "GET",
    url: `${BASE}/task-catalogue/${catalogueId}/match`,
    params: { entity_id: entityId },
  });
}

/** Launch a task from the Command Palette. */
export async function createTask(
  body: CreateTaskRequest,
): Promise<CreateTaskResponse> {
  return apiRequest<CreateTaskResponse>({
    method: "POST",
    url: `${BASE}/tasks`,
    data: body,
  });
}

/** List published mission templates available to the current company. */
export async function listMissionTemplates(): Promise<MissionTemplateSummary[]> {
  return apiRequest<MissionTemplateSummary[]>({
    method: "GET",
    url: `${BASE}/mission-templates`,
  });
}

/** Codex admin catalogue — all tenants, requires admin. */
export async function listCodexAgents(): Promise<AgentSummary[]> {
  return apiRequest<AgentSummary[]>({
    method: "GET",
    url: `${BASE}/codex/agents`,
  });
}

export async function listCodexMissionTemplates(): Promise<MissionTemplateSummary[]> {
  return apiRequest<MissionTemplateSummary[]>({
    method: "GET",
    url: `${BASE}/codex/mission-templates`,
  });
}

export async function getCodexMissionTemplate(
  templateId: string,
): Promise<CodexMissionTemplateDetail> {
  return apiRequest<CodexMissionTemplateDetail>({
    method: "GET",
    url: `${BASE}/codex/mission-templates/${templateId}`,
  });
}

export async function listCodexTools(
  params: ListToolsParams = {},
): Promise<ToolSummary[]> {
  const search: Record<string, string | number> = {};
  if (params.q) search.q = params.q;
  if (params.category) search.category = params.category;
  if (params.type) search.type = params.type;
  if (params.status) search.status = params.status;
  if (params.limit !== undefined) search.limit = params.limit;
  return apiRequest<ToolSummary[]>({
    method: "GET",
    url: `${BASE}/codex/tools`,
    params: search,
  });
}

export async function listCodexTaskCatalogue(
  params: ListTaskCatalogueParams = {},
): Promise<TaskCatalogueItem[]> {
  const search: Record<string, string | number | boolean> = {};
  if (params.q) search.q = params.q;
  if (params.key) search.key = params.key;
  if (params.entity_type) search.entity_type = params.entity_type;
  if (params.limit !== undefined) search.limit = params.limit;
  return apiRequest<TaskCatalogueItem[]>({
    method: "GET",
    url: `${BASE}/codex/task-catalogue`,
    params: search,
  });
}

/** List active agents available to the current company. */
export async function listAgents(): Promise<AgentSummary[]> {
  return apiRequest<AgentSummary[]>({
    method: "GET",
    url: `${BASE}/agents`,
  });
}

/** List agentic v2 tools for the current tenant. */
export async function listTools(params: ListToolsParams = {}): Promise<ToolSummary[]> {
  const search: Record<string, string | number> = {};
  if (params.q) search.q = params.q;
  if (params.category) search.category = params.category;
  if (params.type) search.type = params.type;
  if (params.status) search.status = params.status;
  if (params.limit !== undefined) search.limit = params.limit;
  return apiRequest<ToolSummary[]>({
    method: "GET",
    url: `${BASE}/tools`,
    params: search,
  });
}

/**
 * List clients (minimal shape) for the New Mission picker.
 *
 * Uses the `/picker` sub-route which returns the small `FaClientSummary`
 * shape unchanged from Phase 0.
 */
export async function listFaClients(
  q?: string,
  limit = 200,
  visibility: "me" | "team" | "all" = "me",
): Promise<FaClientSummary[]> {
  return apiRequest<FaClientSummary[]>({
    method: "GET",
    url: `${BASE}/fa-clients/picker`,
    params: { limit, visibility, ...(q ? { q } : {}) },
  });
}

/**
 * List clients for the Clients knowledge-centre page.
 *
 * Includes activity counts (active missions, open tasks, memories) and
 * last-activity timestamp so the list view can show badges without
 * additional round-trips.
 */
export async function listClients(
  params: ListFaClientsParams = {},
  options: { signal?: AbortSignal } = {},
): Promise<FaClientListItem[]> {
  const search: Record<string, string | number> = {};
  if (params.q) search.q = params.q;
  if (params.tag) search.tag = params.tag;
  if (params.risk_profile) search.risk_profile = params.risk_profile;
  if (params.limit !== undefined) search.limit = params.limit;
  if (params.skip !== undefined) search.skip = params.skip;
  if (params.visibility) search.visibility = params.visibility;
  if (params.stage_group) search.stage_group = params.stage_group;
  if (params.onboarding_scope) search.onboarding_scope = params.onboarding_scope;

  return apiRequest<FaClientListItem[]>({
    method: "GET",
    url: `${BASE}/fa-clients`,
    params: search,
    signal: options.signal,
  });
}

/** Caseload + journey stage group counts for Clients list pills. */
export async function getClientsStageSummary(
  params: ClientsStageSummaryParams = {},
  options: { signal?: AbortSignal } = {},
): Promise<FaClientsStageSummary> {
  const search: Record<string, string> = {};
  if (params.visibility) search.visibility = params.visibility;
  if (params.onboarding_scope) search.onboarding_scope = params.onboarding_scope;
  return apiRequest<FaClientsStageSummary>({
    method: "GET",
    url: `${BASE}/fa-clients/stage-summary`,
    params: search,
    signal: options.signal,
  });
}

/** Fetch a single client with all its detail + computed counts. */
export async function getClient(faClientId: string): Promise<FaClientDetail> {
  return apiRequest<FaClientDetail>({
    method: "GET",
    url: `${BASE}/fa-clients/${faClientId}`,
  });
}

/**
 * Edit core lead-record fields (PATCH /fa-clients/{id}).
 * Status and adviser assignment stay workflow-owned and are rejected server-side.
 */
export async function updateFaClient(
  faClientId: string,
  patch: FaClientUpdate,
): Promise<FaClientDetail> {
  return apiRequest<FaClientDetail>({
    method: "PATCH",
    url: `${BASE}/fa-clients/${faClientId}`,
    data: patch,
  });
}

/**
 * List leads for the Leads module page.
 *
 * Tenant-scoped server-side; supports free-text search (name/email) and a
 * lead-status filter (omit for all).
 */
export async function listLeads(
  params: ListLeadsParams = {},
  options: { signal?: AbortSignal } = {},
): Promise<FaLeadListItem[]> {
  const search: Record<string, string | number> = {};
  if (params.q) search.q = params.q;
  if (params.status) search.status = params.status;
  if (params.limit !== undefined) search.limit = params.limit;
  if (params.skip !== undefined) search.skip = params.skip;

  return apiRequest<FaLeadListItem[]>({
    method: "GET",
    url: `${BASE}/fa-clients/leads`,
    params: search,
    signal: options.signal,
  });
}

/** Distinct lead statuses for the Leads list filter chips (tenant-scoped). */
export async function getLeadStatuses(
  options: { signal?: AbortSignal } = {},
): Promise<FaLeadStatuses> {
  return apiRequest<FaLeadStatuses>({
    method: "GET",
    url: `${BASE}/fa-clients/leads/statuses`,
    signal: options.signal,
  });
}

/** Per-household data-gathering status (docs, cheat sheets, phase gate). */
export async function getFaClientDataGathering(
  faClientId: string,
): Promise<FaClientDataGathering> {
  return apiRequest<FaClientDataGathering>({
    method: "GET",
    url: `${BASE}/fa-clients/${faClientId}/data-gathering`,
  });
}

export async function confirmChecklistItem(
  faClientId: string,
  itemId: string,
  action: "confirm" | "reject" | "not_required" = "confirm",
): Promise<FaClientDataGathering> {
  return apiRequest<FaClientDataGathering>({
    method: "POST",
    url: `${BASE}/fa-clients/${faClientId}/data-gathering/checklist/confirm`,
    data: { item_id: itemId, action },
  });
}

/** @deprecated Use confirmChecklistItem */
export async function confirmDgReviewItem(
  faClientId: string,
  itemId: string,
  action: "confirm" | "reject" | "not_required" = "confirm",
): Promise<FaClientDataGathering> {
  return confirmChecklistItem(faClientId, itemId, action);
}

/** Ops tracker — fa_clients in data gathering (excludes lead lost). */
export async function listDataGatheringOps(
  options: { bucket?: DataGatheringOpsBucket; limit?: number } = {},
): Promise<DataGatheringOpsSummary> {
  const { bucket = "upcoming", limit = 200 } = options;
  return apiRequest<DataGatheringOpsSummary>({
    method: "GET",
    url: `${BASE}/data-gathering`,
    params: { bucket, limit },
  });
}

/** Paraplanner book — team-scoped households in data gathering with checklist rollups. */
export async function listDataGatheringBook(
  options: {
    bucket?: DataGatheringOpsBucket;
    limit?: number;
    visibility?: "me" | "team" | "all";
  } = {},
): Promise<DataGatheringOpsSummary> {
  const { bucket = "upcoming", limit = 200, visibility = "team" } = options;
  return apiRequest<DataGatheringOpsSummary>({
    method: "GET",
    url: `${BASE}/data-gathering/book`,
    params: { bucket, limit, visibility },
  });
}

/** Ops tracker — latest plan cheat sheets for in-phase households. */
export async function listDgCheatSheetsOps(
  options: {
    search?: string;
    approval?: "all" | "approved" | "unapproved";
    limit?: number;
  } = {},
): Promise<DgCheatSheetsOpsList> {
  const { search, approval = "all", limit = 500 } = options;
  return apiRequest<DgCheatSheetsOpsList>({
    method: "GET",
    url: `${BASE}/data-gathering/cheat-sheets`,
    params: {
      ...(search ? { search } : {}),
      approval,
      limit,
    },
  });
}

/** Ops tracker — failed or unsupported documents for in-phase households. */
export async function listDgFailedDocumentsOps(
  options: { search?: string; limit?: number } = {},
): Promise<DgFailedDocumentsOpsList> {
  const { search, limit = 500 } = options;
  return apiRequest<DgFailedDocumentsOpsList>({
    method: "GET",
    url: `${BASE}/data-gathering/failed-documents`,
    params: {
      ...(search ? { search } : {}),
      limit,
    },
  });
}

/** Spawn DG monitoring on a household's active mission (ops). */
export async function startDgMonitoring(
  faClientId: string,
): Promise<DgMonitoringActionResult> {
  return apiRequest<DgMonitoringActionResult>({
    method: "POST",
    url: `${BASE}/fa-clients/${faClientId}/data-gathering/start-monitoring`,
  });
}

/** Nudge the DG monitoring agent to recheck status now (clients or ops). */
export async function recheckDgMonitoring(
  faClientId: string,
): Promise<DgMonitoringActionResult> {
  return apiRequest<DgMonitoringActionResult>({
    method: "POST",
    url: `${BASE}/fa-clients/${faClientId}/data-gathering/recheck`,
  });
}

/** Full Plannr household resync — same path as inbound webhooks (ops). */
export async function resyncDgClientPlannr(
  faClientId: string,
): Promise<DgPlannrResyncResult> {
  return apiRequest<DgPlannrResyncResult>({
    method: "POST",
    url: `${BASE}/fa-clients/${faClientId}/data-gathering/resync`,
  });
}

/** Catalog of Pulse V2 workstack views (requires ``clients`` module access). */
export async function listPulseViews(
  options: { signal?: AbortSignal } = {},
): Promise<PulseViewCatalogItem[]> {
  return apiRequest<PulseViewCatalogItem[]>({
    method: "GET",
    url: `${BASE}/pulse-v2/views`,
    signal: options.signal,
  });
}

/** Fetch one Pulse V2 view (table data + column defs). */
export async function getPulseView(
  viewId: string,
  params: GetPulseViewParams = {},
  options: { signal?: AbortSignal } = {},
): Promise<PulseViewResponse> {
  const search: Record<string, string | number> = {};
  if (params.cse_email) search.cse_email = params.cse_email;
  if (params.cse_scope) search.cse_scope = params.cse_scope;
  if (params.adviser_email) search.adviser_email = params.adviser_email;
  if (params.group_value) search.group_value = params.group_value;
  if (params.limit !== undefined) search.limit = params.limit;
  if (params.skip !== undefined) search.skip = params.skip;

  return apiRequest<PulseViewResponse>({
    method: "GET",
    url: `${BASE}/pulse-v2/views/${viewId}`,
    params: search,
    signal: options.signal,
  });
}

/** Assignees for a view's CSE picker (``GET .../views/:viewId/assignees``). */
export async function listPulseViewAssignees(
  viewId: string,
  options: { signal?: AbortSignal } = {},
): Promise<PulseViewAssignee[]> {
  return apiRequest<PulseViewAssignee[]>({
    method: "GET",
    url: `${BASE}/pulse-v2/views/${viewId}/assignees`,
    signal: options.signal,
  });
}

/** Materialize formula fields into ``pulse_v2_client_derived``.
 * Run once before workstack testing, or after ``fa_clients`` changes.
 */
export async function refreshPulseDerived(
  params: PulseDerivedRefreshParams = {},
): Promise<PulseDerivedRefreshResult> {
  const search: Record<string, string | number | boolean> = {};
  if (params.full) search.full = true;
  if (params.limit !== undefined) search.limit = params.limit;

  return apiRequest<PulseDerivedRefreshResult>({
    method: "POST",
    url: `${BASE}/pulse-v2/derived/refresh`,
    params: search,
  });
}

/** Catalog of Pulse V2 dashboards (requires ``clients`` module access). */
export async function listPulseDashboards(
  options: { signal?: AbortSignal } = {},
): Promise<PulseDashboardCatalogItem[]> {
  return apiRequest<PulseDashboardCatalogItem[]>({
    method: "GET",
    url: `${BASE}/pulse-v2/dashboards`,
    signal: options.signal,
  });
}

/** Fetch one Pulse V2 dashboard (KPI cards). */
export async function getPulseDashboard(
  dashboardId: string,
  params: GetPulseDashboardParams = {},
  options: { signal?: AbortSignal } = {},
): Promise<PulseDashboardResponse> {
  const search: Record<string, string> = {};
  if (params.cse_email) search.cse_email = params.cse_email;
  if (params.adviser_email) search.adviser_email = params.adviser_email;

  return apiRequest<PulseDashboardResponse>({
    method: "GET",
    url: `${BASE}/pulse-v2/dashboards/${dashboardId}`,
    params: search,
    signal: options.signal,
  });
}

/** Pack families with data for the caller's tenant (drives the Pulse home catalogue). */
export async function getPulsePackAvailability(
  options: { signal?: AbortSignal } = {},
): Promise<PulsePackAvailability> {
  return apiRequest<PulsePackAvailability>({
    method: "GET",
    url: `${BASE}/pulse-v2/packs`,
    signal: options.signal,
  });
}

/** MI dashboards in one insight pack for the caller's company. */
export async function listPulseMiDashboards(
  packId: string,
  options: { signal?: AbortSignal } = {},
): Promise<PulseMiDashboardSummary[]> {
  return apiRequest<PulseMiDashboardSummary[]>({
    method: "GET",
    url: `${BASE}/pulse-v2/mi/${encodeURIComponent(packId)}/dashboards`,
    signal: options.signal,
  });
}

/** KPIs and chart panels for one MI dashboard. */
export async function getPulseMiDashboard(
  packId: string,
  dashboardId: string,
  options: { signal?: AbortSignal } = {},
): Promise<PulseMiDashboard> {
  return apiRequest<PulseMiDashboard>({
    method: "GET",
    url: `${BASE}/pulse-v2/mi/${encodeURIComponent(packId)}/dashboards/${encodeURIComponent(dashboardId)}`,
    signal: options.signal,
  });
}

/** Rebuild a live MI pack's snapshot from current data. */
export async function refreshPulseMiPack(packId: string): Promise<PulseMiDashboardSummary[]> {
  return apiRequest<PulseMiDashboardSummary[]>({
    method: "POST",
    url: `${BASE}/pulse-v2/mi/${encodeURIComponent(packId)}/refresh`,
    timeout: 120_000,
  });
}

/** Latest AI briefing and the prompt behind it for one MI pack. */
export async function getPulseMiBriefing(
  packId: string,
  options: { signal?: AbortSignal } = {},
): Promise<PulseMiBriefing> {
  return apiRequest<PulseMiBriefing>({
    method: "GET",
    url: `${BASE}/pulse-v2/mi/${encodeURIComponent(packId)}/briefing`,
    signal: options.signal,
  });
}

/** Save the company's briefing prompt; ``null`` restores the default. */
export async function updatePulseMiBriefingPrompt(
  packId: string,
  prompt: string | null,
): Promise<PulseMiBriefing> {
  return apiRequest<PulseMiBriefing>({
    method: "PUT",
    url: `${BASE}/pulse-v2/mi/${encodeURIComponent(packId)}/briefing/prompt`,
    data: { prompt },
  });
}

/** Regenerate the briefing from the pack's current dashboards. */
export async function generatePulseMiBriefing(packId: string): Promise<PulseMiBriefing> {
  return apiRequest<PulseMiBriefing>({
    method: "POST",
    url: `${BASE}/pulse-v2/mi/${encodeURIComponent(packId)}/briefing/generate`,
  });
}

/** Latest journey-stage parity snapshot (Airtable vs Ani audit). */
export async function getPulseJourneyParityLatest(
  scope: "all" | "cse-dg" = "all",
  options: { signal?: AbortSignal } = {},
): Promise<PulseJourneyParitySnapshotResponse> {
  return apiRequest<PulseJourneyParitySnapshotResponse>({
    method: "GET",
    url: `${BASE}/pulse-v2/parity/journey-stage/latest`,
    params: { scope },
    signal: options.signal,
  });
}

/** Latest AI healthcheck for a client (null if never generated). */
export async function getClientInsight(
  faClientId: string,
): Promise<ClientInsight | null> {
  return apiRequest<ClientInsight | null>({
    method: "GET",
    url: `${BASE}/fa-clients/${faClientId}/insight`,
  });
}

/** Regenerate the AI healthcheck for a client (synchronous LLM call). */
export async function refreshClientInsight(
  faClientId: string,
): Promise<ClientInsight> {
  return apiRequest<ClientInsight>({
    method: "POST",
    url: `${BASE}/fa-clients/${faClientId}/insight/refresh`,
  });
}

/** Fetch the unified events + memories timeline for one client. */
export async function listClientTimeline(
  faClientId: string,
  options: { before?: string; limit?: number } = {},
): Promise<ClientTimelineEntry[]> {
  const params: Record<string, string | number> = {};
  if (options.before) params.before = options.before;
  if (options.limit !== undefined) params.limit = options.limit;
  return apiRequest<ClientTimelineEntry[]>({
    method: "GET",
    url: `${BASE}/fa-clients/${faClientId}/timeline`,
    params,
  });
}

/** List memories attached to one client (pinned-first, then newest-first). */
export async function listClientMemories(
  faClientId: string,
  limit = 100,
): Promise<ClientMemory[]> {
  return apiRequest<ClientMemory[]>({
    method: "GET",
    url: `${BASE}/fa-clients/${faClientId}/memories`,
    params: { limit },
  });
}

/** Add a human-authored memory to a client. */
export async function createClientMemory(
  faClientId: string,
  body: CreateClientMemoryRequest,
): Promise<ClientMemory> {
  return apiRequest<ClientMemory>({
    method: "POST",
    url: `${BASE}/fa-clients/${faClientId}/memories`,
    data: body,
  });
}

/** Remove a memory from a client. */
export async function deleteClientMemory(
  faClientId: string,
  memoryId: string,
): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `${BASE}/fa-clients/${faClientId}/memories/${memoryId}`,
  });
}

/**
 * List tasks across missions, bucketed for the action queue.
 *
 * @param bucket - needs_me | blocked | needs_review | working | queued | recent | all
 * @param missionId - Optional, narrow to one mission (used by Mission Detail)
 */
export type NeedsYouRiskFilter = "high" | "medium" | "low";
export type NeedsYouSort = "priority" | "risk" | "waiting" | "recent" | "subtask" | "run_client";
export type NeedsYouSortDirection = "asc" | "desc";

export async function listTasks(
  bucket: TaskBucket = "needs_me",
  options: {
    missionId?: string;
    faClientId?: string;
    limit?: number;
    visibility?: VisibilityScope;
    asUserId?: string;
    assigneeUserId?: string;
    cursor?: string;
    q?: string;
    riskFilter?: NeedsYouRiskFilter;
    sort?: NeedsYouSort;
    sortDirection?: NeedsYouSortDirection;
    surfaceId?: string;
    enrich?: "surface";
  } = {}
): Promise<Page<TaskActionItem>> {
  const params: Record<string, string | number> = { bucket };
  if (options.missionId) params.mission_id = options.missionId;
  if (options.faClientId) params.fa_client_id = options.faClientId;
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.visibility) params.visibility = options.visibility;
  if (options.asUserId) params.as_user_id = options.asUserId;
  if (options.assigneeUserId) params.assignee_user_id = options.assigneeUserId;
  if (options.cursor) params.cursor = options.cursor;
  if (options.q?.trim()) params.q = options.q.trim();
  if (options.riskFilter) params.risk_filter = options.riskFilter;
  if (options.sort) params.sort = options.sort;
  if (options.sortDirection) params.sort_direction = options.sortDirection;
  if (options.surfaceId) params.surface_id = options.surfaceId;
  if (options.enrich === "surface") params.enrich = "surface";
  return apiRequest<Page<TaskActionItem>>({
    method: "GET",
    url: `${BASE}/tasks`,
    params,
  });
}

/** Mission Control: all action-queue buckets in one request. */
export async function listTaskBuckets(
  buckets: TaskBucket[],
  options: {
    missionId?: string;
    faClientId?: string;
    limit?: number;
    visibility?: VisibilityScope;
    asUserId?: string;
    enrich?: "surface";
  } = {}
): Promise<Record<TaskBucket, Page<TaskActionItem>>> {
  const params: Record<string, string | number> = {
    buckets: buckets.join(","),
  };
  if (options.missionId) params.mission_id = options.missionId;
  if (options.faClientId) params.fa_client_id = options.faClientId;
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.visibility) params.visibility = options.visibility;
  if (options.asUserId) params.as_user_id = options.asUserId;
  if (options.enrich === "surface") params.enrich = "surface";
  return apiRequest<Record<TaskBucket, Page<TaskActionItem>>>({
    method: "GET",
    url: `${BASE}/tasks/buckets`,
    params,
  });
}

/** List operational surfaces available to the caller. */
export async function listSurfaces(): Promise<SurfaceListItem[]> {
  return apiRequest<SurfaceListItem[]>({
    method: "GET",
    url: `${BASE}/surfaces`,
  });
}

/** Surface layout spec (widgets + bindings, no heavy data). */
export async function getSurfaceSpec(surfaceId: string): Promise<SurfaceSpec> {
  return apiRequest<SurfaceSpec>({
    method: "GET",
    url: `${BASE}/surfaces/${encodeURIComponent(surfaceId)}`,
  });
}

/** Full surface snapshot for web or mobile clients. */
export async function getSurfaceSnapshot(
  surfaceId: string,
  params: GetSurfaceSnapshotParams = {},
): Promise<SurfaceSnapshot> {
  const query: Record<string, string> = {};
  if (params.visibility) query.visibility = params.visibility;
  if (params.as_user_id) query.as_user_id = params.as_user_id;
  if (params.assignee_user_id) query.assignee_user_id = params.assignee_user_id;
  if (params.platform) query.platform = params.platform;
  return apiRequest<SurfaceSnapshot>({
    method: "GET",
    url: `${BASE}/surfaces/${encodeURIComponent(surfaceId)}/snapshot`,
    params: query,
  });
}

/** Current standup briefing for the logged-in user. */
export async function getStandupBriefing(
  params: GetStandupBriefingParams = {},
): Promise<StandupBriefing> {
  const query: Record<string, string | boolean> = {
    briefing_type: params.briefingType ?? "daily_standup",
  };
  if (params.surfaceId) query.surface_id = params.surfaceId;
  if (params.refresh) query.refresh = true;
  return apiRequest<StandupBriefing>({
    method: "GET",
    url: `${BASE}/standup/current`,
    params: query,
  });
}

/** Force-refresh the standup briefing. */
export async function refreshStandupBriefing(
  params: GetStandupBriefingParams = {},
): Promise<StandupBriefing> {
  const query: Record<string, string> = {
    briefing_type: params.briefingType ?? "daily_standup",
  };
  if (params.surfaceId) query.surface_id = params.surfaceId;
  const response = await apiRequest<{ briefing: StandupBriefing }>({
    method: "POST",
    url: `${BASE}/standup/refresh`,
    params: query,
  });
  return response.briefing;
}

/** Admin: fetch company surface layout override (includes branding). */
export async function getCompanySurfaceLayout(
  companyId: string,
  surfaceId: string,
): Promise<{
  surface_id: string;
  company_id: string;
  override: {
    branding?: {
      logo_url?: string | null;
      logo_url_light?: string | null;
      logo_url_dark?: string | null;
      show_company_name?: boolean;
    };
  };
}> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/surfaces/admin/companies/${encodeURIComponent(companyId)}/layouts/${encodeURIComponent(surfaceId)}`,
  });
}

/** Admin: upload a logo for a company's home / ops surface chrome. */
export async function uploadCompanySurfaceLogo(
  companyId: string,
  surfaceId: string,
  file: File,
  variant: HomeLogoVariant = "dark",
): Promise<SurfaceLogoUploadResult> {
  const formData = new FormData();
  formData.append("file", file);
  return apiRequest<SurfaceLogoUploadResult>({
    method: "POST",
    url: `${BASE}/surfaces/admin/companies/${encodeURIComponent(companyId)}/layouts/${encodeURIComponent(surfaceId)}/branding/logo/${variant}`,
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
}

/** Admin: remove a custom surface logo variant (reverts to fallback or default ANI logo). */
export async function deleteCompanySurfaceLogo(
  companyId: string,
  surfaceId: string,
  variant: HomeLogoVariant = "dark",
): Promise<void> {
  await apiRequest<void>({
    method: "DELETE",
    url: `${BASE}/surfaces/admin/companies/${encodeURIComponent(companyId)}/layouts/${encodeURIComponent(surfaceId)}/branding/logo/${variant}`,
  });
}

/** Accurate Mission Control Activity stat counts (server-side). */
export async function getMissionControlFeedStats(options: {
  visibility?: VisibilityScope;
  as_user_id?: string;
} = {}): Promise<MissionControlFeedStats> {
  const params: Record<string, string> = {};
  if (options.visibility) params.visibility = options.visibility;
  if (options.as_user_id) params.as_user_id = options.as_user_id;
  return apiRequest<MissionControlFeedStats>({
    method: "GET",
    url: `${BASE}/mission-control/feed-stats`,
    params,
  });
}

/** Cursor-paginated merged activity feed for Mission Control. */
export async function getMissionControlFeed(options: {
  visibility?: VisibilityScope;
  as_user_id?: string;
  limit?: number;
  cursor?: string;
  since?: string;
} = {}): Promise<MissionControlFeedPage> {
  const params: Record<string, string | number> = {};
  if (options.visibility) params.visibility = options.visibility;
  if (options.as_user_id) params.as_user_id = options.as_user_id;
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.cursor) params.cursor = options.cursor;
  if (options.since) params.since = options.since;
  const page = await apiRequest<MissionControlFeedPage>({
    method: "GET",
    url: `${BASE}/mission-control/feed`,
    params,
  });
  return {
    items: page.items ?? [],
    next_cursor: page.next_cursor ?? null,
    has_more: page.has_more ?? false,
  };
}

/** Task bucket snapshot — counts and optional capped rows in one round-trip. */
export async function getMissionControlBuckets(options: {
  buckets: TaskBucket[];
  include_items?: boolean;
  limit_per_bucket?: number;
  visibility?: VisibilityScope;
  as_user_id?: string;
  fa_client_id?: string;
}): Promise<MissionControlBucketsResponse> {
  const params: Record<string, string | number | boolean> = {
    buckets: options.buckets.join(","),
  };
  if (options.include_items !== undefined) params.include_items = options.include_items;
  if (options.limit_per_bucket !== undefined) {
    params.limit_per_bucket = options.limit_per_bucket;
  }
  if (options.visibility) params.visibility = options.visibility;
  if (options.as_user_id) params.as_user_id = options.as_user_id;
  if (options.fa_client_id) params.fa_client_id = options.fa_client_id;
  return apiRequest<MissionControlBucketsResponse>({
    method: "GET",
    url: `${BASE}/mission-control/buckets`,
    params,
  });
}

/** Get a single task with its synthesised thread. */
export async function getTask(taskId: string): Promise<TaskDetail> {
  return apiRequest<TaskDetail>({
    method: "GET",
    url: `${BASE}/tasks/${taskId}`,
  });
}

/** Replace the current user's OpenWork letter-review demo queue with a fresh seed. */
export async function reseedOpenworkLetterReviewDemo(): Promise<{
  seeded: boolean;
  assignee: string;
  tasks?: number;
}> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/demo/openwork-letter-review/reseed`,
    data: {},
  });
}

export type OpenworkDemoNotificationDto = {
  id: string;
  kind: "info" | "alert" | "success";
  title: string;
  body: string;
  task_scenario_ref?: string | null;
  created_at?: string | null;
  read: boolean;
  actioned: boolean;
  actioned_at?: string | null;
};

export type OpenworkDemoStateDto = {
  notifications: OpenworkDemoNotificationDto[];
  intro_completed?: boolean;
};

export async function getOpenworkDemoState(): Promise<OpenworkDemoStateDto> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/demo/openwork-letter-review/state`,
  });
}

export async function markOpenworkNotificationRead(notificationId: string): Promise<OpenworkDemoStateDto> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/demo/openwork-letter-review/notifications/read`,
    data: { notification_id: notificationId },
  });
}

export async function markOpenworkNotificationActioned(scenarioRef: string): Promise<OpenworkDemoStateDto> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/demo/openwork-letter-review/notifications/actioned`,
    data: { scenario_ref: scenarioRef },
  });
}

export async function completeOpenworkIntro(): Promise<OpenworkDemoStateDto> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/demo/openwork-letter-review/intro/complete`,
  });
}

export type OpenworkDemoLetterCardDto = {
  task_id: string;
  artifact_id: string | null;
  scenario_ref: string | null;
  client_name: string;
  kind_label: string;
  demo_queue_rank: number | null;
  is_addendum: boolean;
  status: string | null;
  approval_state: string | null;
  updated_at: string | null;
  user_ask: string | null;
  notes: { blocking: number; advisory: number };
  follow_up_actions: { label: string; detail: string }[];
};

export type OpenworkDemoLettersDto = {
  items: OpenworkDemoLetterCardDto[];
};

export async function listOpenworkDemoLetters(): Promise<OpenworkDemoLettersDto> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/demo/openwork-letter-review/letters`,
  });
}

/** Active company users available for task assignment or view-as. */
export async function listAssignableUsers(
  q?: string,
  options: { limit?: number; forViewAs?: boolean } = {}
): Promise<
  { user_id: string; email: string; display_name: string; company_id?: string }[]
> {
  const params: Record<string, string | number | boolean> = {
    limit: options.limit ?? 50,
  };
  if (q && q.trim()) params.q = q.trim();
  if (options.forViewAs) params.for_view_as = true;
  return apiRequest({
    method: "GET",
    url: `${BASE}/tasks/assignable-users`,
    params,
  });
}

/** Assign a task to a human by work email. */
export async function assignTaskHuman(
  taskId: string,
  body: AssignTaskHumanRequest
): Promise<AssignTaskHumanResponse> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/assign-human`,
    data: body,
  });
}

/** Apply a human action (approve/reject/snooze/...) to a task. */
export async function applyTaskAction(
  taskId: string,
  body: ApplyTaskActionRequest
): Promise<TaskDetail> {
  return apiRequest<TaskDetail>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/actions`,
    data: body,
  });
}

/** Save edits made in a review step back to its catalogue data source; returns the reloaded data. */
export async function saveReviewData(
  taskId: string,
  source: string,
  values: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>({
    method: "PUT",
    url: `${BASE}/tasks/${taskId}/review-data/${encodeURIComponent(source)}`,
    data: { values },
  });
}

export async function getFaClientNarrativeSource(
  faClientId: string,
  missionId?: string | null,
): Promise<Record<string, unknown>> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/fa-clients/${faClientId}/narrative-source`,
    params: missionId ? { mission_id: missionId } : undefined,
  });
}

export async function saveFaClientNarrativeSource(
  faClientId: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return apiRequest({
    method: "PUT",
    url: `${BASE}/fa-clients/${faClientId}/narrative-source`,
    data: body,
  });
}

/** Search a review step's data source (e.g. the adviser directory). */
export async function searchReviewData(taskId: string, source: string, q: string): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>({
    method: "GET",
    url: `${BASE}/tasks/${taskId}/review-data/${encodeURIComponent(source)}`,
    params: { q },
  });
}

/** Run a footer action declared on the task's review step. */
export async function runReviewAction(
  taskId: string,
  body: { step: string; action: string; payload?: Record<string, unknown>; rationale?: string },
): Promise<void> {
  await apiRequest<void>({ method: "POST", url: `${BASE}/tasks/${taskId}/review-actions`, data: body });
}

/** Apply the same human action to multiple tasks (batch approve for low-risk only). */
export async function applyTaskActionsBatch(
  body: BatchApplyTaskActionsRequest
): Promise<BatchApplyTaskActionsResponse> {
  return apiRequest<BatchApplyTaskActionsResponse>({
    method: "POST",
    url: `${BASE}/tasks/actions/batch`,
    data: body,
  });
}

/** Pause auto-queue spawning for a mission run. */
export async function pauseMission(missionId: string): Promise<MissionDetail> {
  return apiRequest<MissionDetail>({
    method: "POST",
    url: `${BASE}/missions/${missionId}/pause`,
  });
}

/** Resume a paused mission run. */
export async function resumeMission(missionId: string): Promise<MissionDetail> {
  return apiRequest<MissionDetail>({
    method: "POST",
    url: `${BASE}/missions/${missionId}/resume`,
  });
}

/** Record first review open for audit / time-to-decide. */
export async function recordTaskReviewOpened(taskId: string): Promise<{
  task_id: string;
  reviewer_id: string;
  opened_at: string;
  already_recorded: boolean;
}> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/review-opened`,
  });
}

/** Export JSON evidence pack for HITL audit. */
export async function getTaskEvidencePack(taskId: string): Promise<Record<string, unknown>> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/tasks/${taskId}/evidence-pack`,
  });
}

/**
 * Re-evaluate a gate-blocked task's predecessor condition.
 *
 * If the condition now passes, the task is flipped to ``queued`` and the
 * worker picks it up. If it still fails, ``gate_blocked_reason.actual``
 * is refreshed and the task stays blocked.
 *
 * Only valid on tasks with a ``gate_blocked_reason``; returns 400
 * otherwise. Used by the Task Thread's GateBlockedBanner.
 */
export async function recheckTask(taskId: string): Promise<TaskDetail> {
  return apiRequest<TaskDetail>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/recheck`,
  });
}

/**
 * Bypass a gate-blocked task's gate.
 *
 * Always flips to ``queued`` regardless of the predecessor condition.
 * Records ``gate_override = {by, at, original_reason}`` for audit.
 * Use sparingly — preferred path is to fix the underlying state and
 * call ``recheckTask``.
 */
export async function forceRunTask(taskId: string): Promise<TaskDetail> {
  return apiRequest<TaskDetail>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/force_run`,
  });
}

/** Re-render advice charts for a Generate Advice Pack task (saved plan scope). */
export async function refreshAdviceCharts(taskId: string): Promise<TaskDetail> {
  return apiRequest<TaskDetail>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/refresh-advice-charts`,
  });
}

// ── Step 2: thread messages ────────────────────────────────────────────

/** Fetch the full thread (messages + interactions) for a task. */
export async function getTaskThread(
  taskId: string,
  options: { since?: string } = {},
): Promise<ThreadView> {
  const params: Record<string, string> = {};
  if (options.since) params.since = options.since;
  return apiRequest<ThreadView>({
    method: "GET",
    url: `${BASE}/tasks/${taskId}/messages`,
    params,
  });
}

/** Append an advisor-authored text message to a task thread. */
export async function postTaskMessage(
  taskId: string,
  body: PostMessageRequest
): Promise<ThreadMessage> {
  return apiRequest<ThreadMessage>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/messages`,
    data: body,
  });
}

export interface TaskEntityDocumentItem {
  document_id: string;
  filename: string;
  mime_type?: string | null;
  size_bytes?: number | null;
  uploaded_at?: string | null;
  role?: string | null;
}

export interface IhfSoftCheckPriceItem {
  product_id: string;
  asset_code: string;
  name: string;
  price: number;
  currency: string;
}

export interface IhfUnmappedCodeItem {
  mapping_type: "asset" | "portfolio";
  code: string;
  name?: string | null;
  isin?: string | null;
}

export interface IhfSoftCheckSummary {
  valuation_date?: string | null;
  total_rows: number;
  prices: IhfSoftCheckPriceItem[];
  unmapped_codes: IhfUnmappedCodeItem[];
}

export interface TaskEntityDocumentListResponse {
  template_name?: string | null;
  documents: TaskEntityDocumentItem[];
  soft_check?: IhfSoftCheckSummary | null;
}

/** List files uploaded to the task's entity/mission document index. */
export async function listTaskDocuments(
  taskId: string
): Promise<TaskEntityDocumentListResponse> {
  return apiRequest<TaskEntityDocumentListResponse>({
    method: "GET",
    url: `${BASE}/tasks/${taskId}/documents`,
  });
}

/** Persist an Asset Code or Portfolio Code mapping during capture soft-check. */
export async function saveTaskDocumentMapping(
  taskId: string,
  body: { mapping_type: "asset" | "portfolio"; code: string; resource_id: string }
): Promise<TaskEntityDocumentListResponse> {
  return apiRequest<TaskEntityDocumentListResponse>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/documents/mappings`,
    data: body,
  });
}

/** Delete an uploaded file from the task's entity/mission document index. */
export async function deleteTaskDocument(
  taskId: string,
  documentId: string
): Promise<void> {
  await apiRequest<void>({
    method: "DELETE",
    url: `${BASE}/tasks/${taskId}/documents/${documentId}`,
  });
}

/** Upload a file to the task's entity document index (v2, not pods). */
export async function uploadTaskDocument(
  taskId: string,
  file: File
): Promise<UploadDocumentResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return apiRequest<UploadDocumentResponse>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/documents`,
    data: formData,
  });
}

/** Upload a file to a personal-worker v2 conversation. */
export async function uploadConversationDocument(
  conversationId: string,
  file: File
): Promise<UploadDocumentResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return apiRequest<UploadDocumentResponse>({
    method: "POST",
    url: `${BASE}/conversations/${conversationId}/documents`,
    data: formData,
  });
}

/** Fetch the full thread (messages + interactions) for a mission channel. */
export async function getMissionThread(missionId: string): Promise<ThreadView> {
  return apiRequest<ThreadView>({
    method: "GET",
    url: `${BASE}/missions/${missionId}/messages`,
  });
}

/** Append an advisor-authored text message to a mission channel. */
export async function postMissionMessage(
  missionId: string,
  body: PostMessageRequest
): Promise<ThreadMessage> {
  return apiRequest<ThreadMessage>({
    method: "POST",
    url: `${BASE}/missions/${missionId}/messages`,
    data: body,
  });
}

// ── Artifacts ─────────────────────────────────────────────────────────

/** List artefacts attached to an fa_client (newest first, active-only by default). */
export async function listFaClientArtifacts(
  faClientId: string,
  params: ListArtifactsParams = {}
): Promise<Artifact[]> {
  const search: Record<string, string | number | boolean> = {};
  if (params.kind) search.kind = params.kind;
  if (params.include_archived !== undefined)
    search.include_archived = params.include_archived;
  if (params.limit !== undefined) search.limit = params.limit;
  return apiRequest<Artifact[]>({
    method: "GET",
    url: `${BASE}/fa-clients/${faClientId}/artifacts`,
    params: search,
  });
}

/** List artefacts produced by tasks in a given mission. */
export async function listMissionArtifacts(
  missionId: string,
  limit = 100
): Promise<Artifact[]> {
  return apiRequest<Artifact[]>({
    method: "GET",
    url: `${BASE}/missions/${missionId}/artifacts`,
    params: { limit },
  });
}

/** List all artefacts across the current tenant. */
export async function listArtifacts(
  params: ListArtifactsParams = {},
): Promise<Page<Artifact>> {
  return apiRequest<Page<Artifact>>({
    method: "GET",
    url: `${BASE}/artifacts`,
    params,
  });
}

/** Count artefacts matching list filters (e.g. last-24h badge). */
export async function countArtifacts(
  params: Omit<ListArtifactsParams, "limit" | "cursor"> = {},
): Promise<number> {
  const result = await apiRequest<{ count: number }>({
    method: "GET",
    url: `${BASE}/artifacts/count`,
    params,
  });
  return result.count;
}

/** Fetch a single artefact by id. */
export async function getArtifact(artifactId: string): Promise<Artifact> {
  return apiRequest<Artifact>({
    method: "GET",
    url: `${BASE}/artifacts/${artifactId}`,
  });
}

/** Fetch the artefact's Azure-backed PDF bytes through the API proxy. */
export async function fetchArtifactPdfBlob(artifactId: string): Promise<Blob> {
  return apiRequest<Blob>({
    method: "GET",
    url: `${BASE}/artifacts/${artifactId}/download`,
    responseType: "blob",
  });
}

/** Extract plain text from a stored artefact file (e.g. advisor-notes DOCX). */
export async function fetchArtifactPreviewText(
  artifactId: string,
): Promise<{ text: string; notice?: string | null }> {
  return apiRequest<{ text: string; notice?: string | null }>({
    method: "GET",
    url: `${BASE}/artifacts/${artifactId}/preview-text`,
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Open TipTap / form-backed artefact text in a new browser tab. */
export async function openDocumentTextArtifactPreview(
  artifactId: string,
  title?: string | null,
): Promise<void> {
  const { text, notice } = await fetchArtifactPreviewText(artifactId);
  const safeTitle = escapeHtml(title?.trim() || "Document preview");
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${safeTitle}</title>
  <style>
    body { font-family: system-ui, sans-serif; max-width: 48rem; margin: 2rem auto; padding: 0 1rem; line-height: 1.6; color: #1e293b; }
    .notice { background: #fffbeb; border: 1px solid #fcd34d; padding: 0.75rem 1rem; border-radius: 0.5rem; margin-bottom: 1rem; font-size: 0.875rem; }
    pre { white-space: pre-wrap; font-family: inherit; margin: 0; }
  </style>
</head>
<body>
  ${notice ? `<p class="notice">${escapeHtml(notice)}</p>` : ""}
  <pre>${escapeHtml(text || "No text available.")}</pre>
</body>
</html>`;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (!opened) {
    URL.revokeObjectURL(objectUrl);
    throw new Error("Popup blocked — allow popups to open the file preview.");
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/** Open a stored artefact file in a new tab (DOCX, PDF, etc.). */
export async function openStoredArtifactPreview(
  artifactId: string,
  mimeType?: string | null,
  title?: string | null,
): Promise<void> {
  try {
    const blob = await fetchArtifactPdfBlob(artifactId);
    const objectUrl = URL.createObjectURL(
      blob.type ? blob : new Blob([blob], { type: mimeType || "application/octet-stream" }),
    );
    const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
    if (!opened) {
      URL.revokeObjectURL(objectUrl);
      throw new Error("Popup blocked — allow popups to open the file preview.");
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    return;
  } catch (err) {
    if (!axios.isAxiosError(err) || err.response?.status !== 400) {
      throw err;
    }
  }
  await openDocumentTextArtifactPreview(artifactId, title);
}

/**
 * Trigger a browser download of the artefact's Azure-backed bytes.
 *
 * Direct Azure blob URLs require auth, so the API proxies the stream.
 * We fetch as a blob (sending the auth header automatically), create
 * an object URL, and click a hidden anchor to save it.
 */
export async function downloadArtifact(
  artifact: Pick<Artifact, "_id" | "filename">
): Promise<void> {
  const blob = await fetchArtifactPdfBlob(artifact._id);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = artifact.filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

// ── Artifact document content + comments ─────────────────────────────

/** Fetch the latest (or specific version) of an artefact's editable content. */
export async function getArtifactDocument(
  artifactId: string,
  version?: number
): Promise<ArtifactDocumentJoined> {
  return apiRequest<ArtifactDocumentJoined>({
    method: "GET",
    url: `${BASE}/artifacts/${artifactId}/document`,
    params: version !== undefined ? { version } : undefined,
  });
}

/** Save a new version of an artefact's TipTap content (advisor edit). */
export async function saveArtifactDocument(
  artifactId: string,
  content_json: Record<string, unknown>,
  revision_note?: string
): Promise<ArtifactDocumentJoined> {
  return apiRequest<ArtifactDocumentJoined>({
    method: "PATCH",
    url: `${BASE}/artifacts/${artifactId}/document`,
    data: { content_json, revision_note },
  });
}

/**
 * Apply surgical row edits to a ``table`` artefact.
 *
 * Upserts are matched against existing rows by ``row_id`` and deep-
 * merged (``cells`` and ``enrichments`` are merged key-by-key). Rows
 * the caller doesn't mention are left untouched — used for per-row
 * enrichers like the holdings matcher.
 */
export async function patchTableRows(
  artifactId: string,
  input: {
    upserts?: Array<Record<string, unknown>>;
    deletes?: string[];
    revision_note?: string;
  }
): Promise<ArtifactDocumentJoined> {
  return apiRequest<ArtifactDocumentJoined>({
    method: "PATCH",
    url: `${BASE}/artifacts/${artifactId}/table-rows`,
    data: {
      upserts: input.upserts ?? [],
      deletes: input.deletes ?? [],
      revision_note: input.revision_note,
    },
  });
}

/** List version metadata for an artefact (newest first, no content). */
export async function listArtifactVersions(
  artifactId: string
): Promise<ArtifactDocumentVersion[]> {
  return apiRequest<ArtifactDocumentVersion[]>({
    method: "GET",
    url: `${BASE}/artifacts/${artifactId}/versions`,
  });
}

/** List comments on an artefact, optionally scoped to a version. */
export async function listArtifactComments(
  artifactId: string,
  params?: { version?: number; only_open?: boolean }
): Promise<ArtifactComment[]> {
  return apiRequest<ArtifactComment[]>({
    method: "GET",
    url: `${BASE}/artifacts/${artifactId}/comments`,
    params,
  });
}

/** Add a comment anchored to a TipTap selection inside a document version. */
export async function addArtifactComment(
  artifactId: string,
  input: AddArtifactCommentInput
): Promise<ArtifactComment> {
  return apiRequest<ArtifactComment>({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/comments`,
    data: input,
  });
}

/** Flip a comment between open / resolved / addressed. */
export async function setArtifactCommentStatus(
  artifactId: string,
  commentId: string,
  status: ArtifactCommentStatus
): Promise<ArtifactComment> {
  return apiRequest<ArtifactComment>({
    method: "PATCH",
    url: `${BASE}/artifacts/${artifactId}/comments/${commentId}`,
    data: { status },
  });
}

/** Add a reply to an existing artefact comment thread. */
export async function addArtifactCommentReply(
  artifactId: string,
  commentId: string,
  text: string,
): Promise<ArtifactComment> {
  return apiRequest<ArtifactComment>({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/comments/${commentId}/replies`,
    data: { text },
  });
}

/** Update a pending user reply before Send for revision. */
export async function updateArtifactCommentReply(
  artifactId: string,
  commentId: string,
  replyId: string,
  text: string,
): Promise<ArtifactComment> {
  return apiRequest<ArtifactComment>({
    method: "PATCH",
    url: `${BASE}/artifacts/${artifactId}/comments/${commentId}/replies/${replyId}`,
    data: { text },
  });
}

/** Delete a pending user reply before Send for revision. */
export async function deleteArtifactCommentReply(
  artifactId: string,
  commentId: string,
  replyId: string,
): Promise<ArtifactComment> {
  return apiRequest<ArtifactComment>({
    method: "DELETE",
    url: `${BASE}/artifacts/${artifactId}/comments/${commentId}/replies/${replyId}`,
  });
}

/** Redraft a letter from open comments (+ optional chat) without a mission task. */
export async function reviseArtifactFromReview(
  artifactId: string,
): Promise<ArtifactDocumentJoined & { addressed_comment_ids?: string[]; replied_comment_ids?: string[] }> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/revise-from-review`,
  });
}

/** List persisted Ask Ani chat messages for an artefact. */
export async function listArtifactChatMessages(
  artifactId: string,
): Promise<ArtifactChatMessage[]> {
  return apiRequest<ArtifactChatMessage[]>({
    method: "GET",
    url: `${BASE}/artifacts/${artifactId}/chat`,
  });
}

/** Start a fresh Ask Ani thread for this artefact. */
export async function startNewArtifactAniChat(artifactId: string): Promise<void> {
  await apiRequest({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/chat/new`,
  });
}

/** Send a message to Ask Ani and receive Ani's reply. */
export async function askArtifactAni(
  artifactId: string,
  input: AskAniInput,
): Promise<ArtifactChatMessage> {
  return apiRequest<ArtifactChatMessage>({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/ask`,
    data: {
      text: input.text,
      intent: input.intent,
    },
  });
}

/** Apply pending comment suggestions from a specific Ask Ani agent reply. */
export async function applyArtifactChatPendingComments(
  artifactId: string,
  messageId: string,
  input?: { intent?: AskAniIntent },
): Promise<SuggestArtifactCommentsResult> {
  return apiRequest<SuggestArtifactCommentsResult>({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/chat/messages/${messageId}/apply-comments`,
    data: { intent: input?.intent ?? null },
  });
}

/** Run a structured proofread / tone / formatting pass as anchored comments. */
export async function suggestArtifactComments(
  artifactId: string,
  input: SuggestArtifactCommentsInput,
): Promise<SuggestArtifactCommentsResult> {
  return apiRequest<SuggestArtifactCommentsResult>({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/suggest-comments`,
    data: {
      intent: input.intent,
      scope_text: input.scopeText ?? null,
    },
  });
}

/** Approve the artefact's current version — locks in the content and renders DOCX. */
export async function approveArtifact(
  artifactId: string,
  feedback?: string
): Promise<Artifact> {
  return apiRequest<Artifact>({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/approve`,
    data: { feedback },
  });
}

// ── Form schemas (fields only) ────────────────────────────────────

/** Optional tenant scope for schema reads (ani staff cross-tenant). */
export interface SchemaReadOptions {
  /** Artefact / schema owner tenant — required when ani staff view another client. */
  clientId?: string;
}

function schemaReadParams(
  options?: SchemaReadOptions,
): Record<string, string> | undefined {
  if (!options?.clientId) {
    return undefined;
  }
  return { client_id: options.clientId };
}

/** Fetch the latest published form schema for ``key``. */
export async function getFormSchema(
  key: string,
  options?: SchemaReadOptions,
): Promise<FormSchema> {
  return apiRequest<FormSchema>({
    method: "GET",
    url: `${BASE}/artifacts/form-schemas/${key}`,
    params: schemaReadParams(options),
  });
}

/** List the latest version of every form schema for the tenant. */
export async function listFormSchemas(
  options?: SchemaReadOptions,
): Promise<FormSchema[]> {
  return apiRequest<FormSchema[]>({
    method: "GET",
    url: `${BASE}/artifacts/form-schemas`,
    params: schemaReadParams(options),
  });
}

/** Payload for creating a brand-new form schema at version 1. */
export interface CreateFormSchemaInput {
  key: string;
  name: string;
  description?: string | null;
  fields?: Array<Record<string, unknown>>;
  categories?: Record<string, string>;
  applies_to?: Record<string, unknown>;
}

/** Create a brand-new form schema. ``key`` must be unique per tenant. */
export async function createFormSchema(
  body: CreateFormSchemaInput,
): Promise<FormSchema> {
  return apiRequest<FormSchema>({
    method: "POST",
    url: `${BASE}/artifacts/form-schemas`,
    data: body,
  });
}

/** Payload for appending a new version of an existing form schema. */
export interface ReviseFormSchemaInput {
  fields?: Array<Record<string, unknown>> | null;
  name?: string | null;
  description?: string | null;
  categories?: Record<string, string> | null;
  applies_to?: Record<string, unknown> | null;
}

/** Append a new version of an existing form schema. Returns the new row. */
export async function reviseFormSchema(
  key: string,
  body: ReviseFormSchemaInput,
): Promise<FormSchema> {
  return apiRequest<FormSchema>({
    method: "POST",
    url: `${BASE}/artifacts/form-schemas/${key}/revise`,
    data: body,
  });
}

// ── Document templates (TipTap, optionally schema-linked) ─────────

/** Fetch the latest published document template for ``key``. */
export async function getDocumentTemplate(
  key: string,
  options?: SchemaReadOptions,
): Promise<DocumentTemplate> {
  return apiRequest<DocumentTemplate>({
    method: "GET",
    url: `${BASE}/artifacts/document-templates/${key}`,
    params: schemaReadParams(options),
  });
}

/** List the latest version of every document template for the tenant. */
export async function listDocumentTemplates(
  options?: SchemaReadOptions,
): Promise<DocumentTemplate[]> {
  return apiRequest<DocumentTemplate[]>({
    method: "GET",
    url: `${BASE}/artifacts/document-templates`,
    params: schemaReadParams(options),
  });
}

/** Payload for creating a brand-new document template at version 1. */
export interface CreateDocumentTemplateInput {
  key: string;
  artifact_kind: string;
  name: string;
  description?: string | null;
  form_schema_key?: string | null;
  form_schema_version?: number | null;
  content?: Record<string, unknown> | null;
  docx_template?: string | null;
}

/** Create a brand-new document template. */
export async function createDocumentTemplate(
  body: CreateDocumentTemplateInput,
): Promise<DocumentTemplate> {
  return apiRequest<DocumentTemplate>({
    method: "POST",
    url: `${BASE}/artifacts/document-templates`,
    data: body,
  });
}

/** Payload for appending a new version of an existing document template. */
export interface ReviseDocumentTemplateInput {
  content?: Record<string, unknown> | null;
  name?: string | null;
  description?: string | null;
  form_schema_key?: string | null;
  form_schema_version?: number | null;
  docx_template?: string | null;
}

/** Append a new version of an existing document template. */
export async function reviseDocumentTemplate(
  key: string,
  body: ReviseDocumentTemplateInput,
): Promise<DocumentTemplate> {
  return apiRequest<DocumentTemplate>({
    method: "POST",
    url: `${BASE}/artifacts/document-templates/${key}/revise`,
    data: body,
  });
}

// ── Table schemas (columns only) ──────────────────────────────────

/** Fetch the latest published table schema for ``key``. */
export async function getTableSchema(
  key: string,
  options?: SchemaReadOptions,
): Promise<TableSchema> {
  return apiRequest<TableSchema>({
    method: "GET",
    url: `${BASE}/artifacts/table-schemas/${key}`,
    params: schemaReadParams(options),
  });
}

/** List the latest version of every table schema for the tenant. */
export async function listTableSchemas(
  options?: SchemaReadOptions,
): Promise<TableSchema[]> {
  return apiRequest<TableSchema[]>({
    method: "GET",
    url: `${BASE}/artifacts/table-schemas`,
    params: schemaReadParams(options),
  });
}

// ── Checklist schemas ─────────────────────────────────────────────

/** Fetch the latest published checklist schema for ``key``. */
export async function getChecklistSchema(
  key: string,
  options?: SchemaReadOptions,
): Promise<ChecklistSchema> {
  return apiRequest<ChecklistSchema>({
    method: "GET",
    url: `${BASE}/artifacts/checklist-schemas/${key}`,
    params: schemaReadParams(options),
  });
}

/** List the latest version of every checklist schema for the tenant. */
export async function listChecklistSchemas(
  options?: SchemaReadOptions,
): Promise<ChecklistSchema[]> {
  return apiRequest<ChecklistSchema[]>({
    method: "GET",
    url: `${BASE}/artifacts/checklist-schemas`,
    params: schemaReadParams(options),
  });
}

/** Payload for appending a new version of an existing checklist schema. */
export interface ReviseChecklistSchemaInput {
  sections?: ChecklistSchemaSection[] | null;
  name?: string | null;
  description?: string | null;
  applies_to?: Record<string, unknown> | null;
}

/** Append a new version of an existing checklist schema. */
export async function reviseChecklistSchema(
  key: string,
  body: ReviseChecklistSchemaInput,
): Promise<ChecklistSchema> {
  return apiRequest<ChecklistSchema>({
    method: "POST",
    url: `${BASE}/artifacts/checklist-schemas/${key}/revise`,
    data: body,
  });
}

/** Payload for creating a brand-new table schema at version 1. */
export interface CreateTableSchemaInput {
  key: string;
  name: string;
  description?: string | null;
  columns?: Array<Record<string, unknown>>;
  applies_to?: Record<string, unknown>;
}

/** Create a brand-new table schema. ``key`` must be unique per tenant. */
export async function createTableSchema(
  body: CreateTableSchemaInput,
): Promise<TableSchema> {
  return apiRequest<TableSchema>({
    method: "POST",
    url: `${BASE}/artifacts/table-schemas`,
    data: body,
  });
}

/** Payload for appending a new version of an existing table schema. */
export interface ReviseTableSchemaInput {
  columns?: Array<Record<string, unknown>> | null;
  name?: string | null;
  description?: string | null;
  applies_to?: Record<string, unknown> | null;
}

/** Append a new version of an existing table schema. */
export async function reviseTableSchema(
  key: string,
  body: ReviseTableSchemaInput,
): Promise<TableSchema> {
  return apiRequest<TableSchema>({
    method: "POST",
    url: `${BASE}/artifacts/table-schemas/${key}/revise`,
    data: body,
  });
}

// ── Form-driven artefacts (LOAs etc.) ─────────────────────────────

/** Create one per-plan LOA artefact. Returns the joined record. */
export async function createLoa(
  body: CreateLoaInput,
): Promise<
  ArtifactDocumentJoined & {
    form_schema?: FormSchema;
    document_template?: DocumentTemplate;
  }
> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/artifacts/loa`,
    data: body,
  });
}

/** Adviser edit on a form-driven artefact: bumps version + re-renders. */
export async function saveArtifactFormData(
  artifactId: string,
  formData: Record<string, unknown>,
  revisionNote?: string,
): Promise<ArtifactDocumentJoined> {
  return apiRequest<ArtifactDocumentJoined>({
    method: "PATCH",
    url: `${BASE}/artifacts/${artifactId}/form-data`,
    data: { form_data: formData, revision_note: revisionNote },
  });
}

/** Re-render an artefact against the latest document template version. */
export async function relinkArtifactToLatestTemplate(
  artifactId: string,
): Promise<
  ArtifactDocumentJoined & {
    form_schema?: FormSchema;
    document_template?: DocumentTemplate;
  }
> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/relink-template`,
  });
}

/** Agent-driven regenerate: same as save but tagged ``source=agent``. */
export async function regenerateArtifactFormData(
  artifactId: string,
  formData: Record<string, unknown>,
  revisionNote?: string,
): Promise<ArtifactDocumentJoined> {
  return apiRequest<ArtifactDocumentJoined>({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/regenerate`,
    data: { form_data: formData, revision_note: revisionNote },
  });
}

/** Reopen an approved artefact so the adviser can keep editing. */
export async function reopenArtifact(
  artifactId: string,
  reason?: string
): Promise<Artifact> {
  return apiRequest<Artifact>({
    method: "POST",
    url: `${BASE}/artifacts/${artifactId}/reopen`,
    data: { reason },
  });
}

/** Stream a freshly-rendered DOCX of the artefact's latest content. */
export async function renderArtifactDocxBlob(
  artifactId: string,
  contentJson?: Record<string, unknown> | null,
): Promise<Blob> {
  if (contentJson) {
    return apiRequest<Blob>({
      method: "POST",
      url: `${BASE}/artifacts/${artifactId}/render.docx`,
      data: { content_json: contentJson },
      responseType: "blob",
    });
  }
  return apiRequest<Blob>({
    method: "GET",
    url: `${BASE}/artifacts/${artifactId}/render.docx`,
    responseType: "blob",
  });
}

/** Download a freshly-rendered DOCX (uses POST when ``contentJson`` is provided). */
export async function downloadArtifactDocx(
  artifact: Pick<Artifact, "_id" | "filename">,
  contentJson?: Record<string, unknown> | null,
): Promise<void> {
  const blob = await renderArtifactDocxBlob(artifact._id, contentJson);
  triggerArtifactDownload(
    blob,
    ensureArtifactFilename(artifact.filename, "document", "docx"),
  );
}

/** Stream a freshly-rendered XLSX of a form-driven artefact. */
export async function renderArtifactXlsxBlob(artifactId: string): Promise<Blob> {
  return apiRequest<Blob>({
    method: "GET",
    url: `${BASE}/artifacts/${artifactId}/render.xlsx`,
    responseType: "blob",
  });
}

/** Download a freshly-rendered XLSX of a form-driven artefact. */
export async function downloadArtifactXlsx(
  artifact: Pick<Artifact, "_id" | "filename">,
): Promise<void> {
  const blob = await renderArtifactXlsxBlob(artifact._id);
  triggerArtifactDownload(
    blob,
    ensureArtifactFilename(artifact.filename, "document", "xlsx"),
  );
}

export type ArtifactFormExportFormat = "docx" | "xlsx";

/** Download using the mission template's configured form export format. */
export async function downloadArtifactFormExport(
  artifact: Pick<Artifact, "_id" | "filename">,
  format: ArtifactFormExportFormat = "docx",
  contentJson?: Record<string, unknown> | null,
): Promise<void> {
  if (format === "xlsx") {
    await downloadArtifactXlsx(artifact);
    return;
  }
  await downloadArtifactDocx(artifact, contentJson);
}

function ensureArtifactFilename(
  filename: string | null | undefined,
  fallbackBase: string,
  extension: "docx" | "xlsx",
): string {
  const base = filename || `${fallbackBase}.${extension}`;
  const lower = base.toLowerCase();
  if (lower.endsWith(`.${extension}`)) {
    return base;
  }
  const stem = base.includes(".") ? base.replace(/\.[^.]+$/, "") : base;
  return `${stem}.${extension}`;
}

function triggerArtifactDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/** Patch one checklist item (confirm or adviser edit). */
export async function patchChecklistArtifactItem(
  artifactId: string,
  itemId: string,
  body: {
    action?: "confirm" | "reject" | "not_required" | "update";
    status?: ChecklistItemStatus;
    evidence?: string | null;
    revision_note?: string;
  },
): Promise<ArtifactDocumentJoined> {
  return apiRequest<ArtifactDocumentJoined>({
    method: "PATCH",
    url: `${BASE}/artifacts/checklist/${artifactId}/items/${encodeURIComponent(itemId)}`,
    data: body,
  });
}

// ── CRM field registry ────────────────────────────────────────────────

/** List CRM field definitions for the current tenant (optionally one CRM). */
export async function listCrmFields(crm?: string): Promise<CrmField[]> {
  return apiRequest<CrmField[]>({
    method: "GET",
    url: `${BASE}/crm-fields`,
    params: crm ? { crm } : undefined,
  });
}

// ── Trigger rules + events (ops/triggers Plannr tab) ──────────────────

/** List all configured trigger rules for the current tenant. */
export async function listTriggerRules(): Promise<TriggerRule[]> {
  return apiRequest<TriggerRule[]>({
    method: "GET",
    url: `${BASE}/triggers/rules`,
  });
}

/** List recent Plannr webhook / probe / replay runs. */
export async function listPlannrWebhookRuns(
  limit: number = 50
): Promise<PlannrWebhookRun[]> {
  return apiRequest<PlannrWebhookRun[]>({
    method: "GET",
    url: `${BASE}/triggers/webhook-runs`,
    params: { limit },
  });
}

/** List recent trigger events with their nested per-rule outcomes. */
export async function listTriggerEvents(
  limit: number = 50
): Promise<TriggerEventRow[]> {
  return apiRequest<TriggerEventRow[]>({
    method: "GET",
    url: `${BASE}/triggers/events`,
    params: { limit },
  });
}

/** Fetch a single trigger event by id (same shape as `listTriggerEvents` rows). */
export async function getTriggerEvent(
  eventId: string
): Promise<TriggerEventRow> {
  return apiRequest<TriggerEventRow>({
    method: "GET",
    url: `${BASE}/triggers/events/${encodeURIComponent(eventId)}`,
  });
}

/** Fetch a single interaction by id (steps hydrated; ops/staff deep links). */
export async function getInteraction(
  interactionId: string
): Promise<ThreadInteraction> {
  return apiRequest<ThreadInteraction>({
    method: "GET",
    url: `${BASE}/interactions/${encodeURIComponent(interactionId)}`,
  });
}

/** Matched trigger actions awaiting ops approval. */
export async function listTriggerPendingActions(
  limit: number = 100
): Promise<TriggerPendingAction[]> {
  return apiRequest<TriggerPendingAction[]>({
    method: "GET",
    url: `${BASE}/triggers/pending`,
    params: { limit },
  });
}

export async function approveTriggerPendingAction(
  pendingId: string
): Promise<{ status: string; pending_id: string }> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/triggers/pending/${pendingId}/approve`,
  });
}

export async function rejectTriggerPendingAction(
  pendingId: string,
  note?: string
): Promise<{ status: string; pending_id: string }> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/triggers/pending/${pendingId}/reject`,
    data: note ? { note } : undefined,
  });
}

/** Create a trigger rule (client self-serve). */
export async function createTriggerRule(
  body: Omit<TriggerRule, "_id" | "client_id" | "created_at" | "updated_at" | "version">
): Promise<TriggerRule> {
  return apiRequest<TriggerRule>({
    method: "POST",
    url: `${BASE}/triggers/rules`,
    data: body,
  });
}

/** Update a trigger rule; server bumps version for determinism audit. */
export async function updateTriggerRule(
  ruleId: string,
  patch: Partial<
    Pick<
      TriggerRule,
      | "enabled"
      | "event_types"
      | "scope"
      | "condition"
      | "action"
      | "priority"
      | "requires_approval"
    >
  >
): Promise<TriggerRule> {
  return apiRequest<TriggerRule>({
    method: "PATCH",
    url: `${BASE}/triggers/rules/${encodeURIComponent(ruleId)}`,
    data: patch,
  });
}

/** Delete a trigger rule by ID. */
export async function deleteTriggerRule(ruleId: string): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `${BASE}/triggers/rules/${encodeURIComponent(ruleId)}`,
  });
}

/** Event type metadata for the rule editor event type picker. */
export interface EventTypeDomain {
  domain: string;
  label: string;
  /** All Curo ingress types Curo rules register against (condition-only matching). */
  listen_event_types?: string[];
  event_types: Array<{
    value: string;
    label: string;
    description: string;
  }>;
}

/** Keep aligned with ``trigger_event.CURO_RULE_LISTEN_EVENT_TYPES``. */
export const CURO_RULE_LISTEN_EVENT_TYPES = [
  "curo.task.status_changed",
  "curo.contact.updated",
  "curo.plan.created",
  "curo.plan.updated",
] as const;

/** List available event types grouped by domain. */
export async function listEventTypes(): Promise<EventTypeDomain[]> {
  return apiRequest<EventTypeDomain[]>({
    method: "GET",
    url: `${BASE}/triggers/event-types`,
  });
}

/** Condition field metadata for the rule editor condition builder. */
export interface ConditionFieldCategory {
  category: string;
  fields: Array<{
    path: string;
    label: string;
    type: string;
    description: string;
    examples?: string[];
  }>;
}

/** List available condition fields, optionally filtered by event type. */
export async function listConditionFields(
  eventType?: string
): Promise<ConditionFieldCategory[]> {
  return apiRequest<ConditionFieldCategory[]>({
    method: "GET",
    url: `${BASE}/triggers/condition-fields`,
    params: eventType ? { event_type: eventType } : undefined,
  });
}

/** Action type metadata for the rule editor. */
export interface ActionTypeOption {
  value: string;
  label: string;
  description: string;
  requires_existing_mission: boolean;
  config_schema: Record<
    string,
    { type: string; label: string; required: boolean; description?: string }
  >;
}

/** List available action types for trigger rules. */
export async function listActionTypes(): Promise<ActionTypeOption[]> {
  return apiRequest<ActionTypeOption[]>({
    method: "GET",
    url: `${BASE}/triggers/action-types`,
  });
}

/** Mission template summary for the rule editor mission picker. */
export interface MissionTemplateForRule {
  id: string;
  name: string;
  description: string;
  stage_count: number;
}

/** List mission templates available for start_mission actions. */
export async function listMissionTemplatesForRules(): Promise<
  MissionTemplateForRule[]
> {
  return apiRequest<MissionTemplateForRule[]>({
    method: "GET",
    url: `${BASE}/triggers/mission-templates`,
  });
}

export async function listPlannrForms(params: {
  account_uuid: string;
  title_contains?: string;
  status?: string;
}): Promise<PlannrFormsListResponse> {
  return apiRequest<PlannrFormsListResponse>({
    method: "POST",
    url: `${BASE}/plannr/forms`,
    data: params,
  });
}

export async function readPlannrFormSubmission(params: {
  form_uuid: string;
  submission_uuid: string;
  format_for_prompt?: boolean;
}): Promise<PlannrFormSubmissionResponse> {
  return apiRequest<PlannrFormSubmissionResponse>({
    method: "POST",
    url: `${BASE}/plannr/form-submission`,
    data: {
      format_for_prompt: true,
      ...params,
    },
  });
}

export type PatchOnboardingCaptureFieldsInput = {
  fa_client_id: string;
  fields: Record<string, string | number>;
  form_artifact_id?: string;
};

export type PatchOnboardingCaptureFieldsResponse = {
  fa_client_id: string;
  display_name: string;
  mandatory_missing: string[];
};

export async function patchOnboardingCaptureFields(
  body: PatchOnboardingCaptureFieldsInput,
): Promise<PatchOnboardingCaptureFieldsResponse> {
  return apiRequest<PatchOnboardingCaptureFieldsResponse>({
    method: "POST",
    url: `${BASE}/onboarding/patch-capture-fields`,
    data: body,
  });
}

export type BridgeIntakeInput = {
  channel: "voice" | "email" | "transcript";
  mission_id?: string;
  text?: string;
  transcript?: string;
  meeting_id?: string;
  ref_id?: string;
  subject?: string;
  from_address?: string;
  source?: string;
};

export type BridgeIntakeResponse = {
  mission_id: string;
  fa_client_id?: string | null;
  task_id?: string | null;
  skipped?: boolean;
  onboarding_intake_channel: string;
  onboarding_intake_text: string;
  onboarding_intake_source: string;
  onboarding_intake_ref_id?: string | null;
  onboarding_intake_subject?: string | null;
  onboarding_intake_from?: string | null;
  onboarding_voice_transcript?: string | null;
  onboarding_voice_meeting_id?: string | null;
  onboarding_voice_source?: string | null;
};

export async function bridgeOnboardingIntake(
  body: BridgeIntakeInput,
): Promise<BridgeIntakeResponse> {
  return apiRequest<BridgeIntakeResponse>({
    method: "POST",
    url: `${BASE}/onboarding/intake`,
    data: body,
  });
}

export async function bridgeOnboardingIntakeEml(
  file: File,
  missionId?: string,
): Promise<BridgeIntakeResponse> {
  const formData = new FormData();
  formData.append("file", file);
  const query = missionId ? `?mission_id=${encodeURIComponent(missionId)}` : "";
  return apiRequest<BridgeIntakeResponse>({
    method: "POST",
    url: `${BASE}/onboarding/intake/eml${query}`,
    data: formData,
  });
}

export async function bridgeOnboardingIntakeAudio(
  file: File,
  missionId?: string,
): Promise<BridgeIntakeResponse> {
  const formData = new FormData();
  formData.append("file", file);
  const query = missionId ? `?mission_id=${encodeURIComponent(missionId)}` : "";
  return apiRequest<BridgeIntakeResponse>({
    method: "POST",
    url: `${BASE}/onboarding/intake/audio${query}`,
    data: formData,
  });
}

export type ManualCreateLeadInput = {
  title?: string;
  first_name: string;
  last_name: string;
  email_address?: string;
  mobile_telephone?: string;
  home_telephone?: string;
  postcode?: string;
  address?: string;
  adviser_id?: string;
  proposition?: string;
  lead_type?: string;
  lead_source?: string;
  opportunity_value?: string | number;
  rating?: string;
  processing_consent?: boolean;
  secondary_contact_first_name?: string;
  secondary_contact_last_name?: string;
  notes?: string;
};

export async function createManualOnboardingLead(
  body: ManualCreateLeadInput,
): Promise<BridgeIntakeResponse> {
  return apiRequest<BridgeIntakeResponse>({
    method: "POST",
    url: `${BASE}/onboarding/leads/manual`,
    data: body,
  });
}

export type OnboardingAdviserOption = {
  value: string;
  label: string;
};

export type OnboardingAdviserListResponse = {
  items: OnboardingAdviserOption[];
};

export async function getOnboardingAdvisers(): Promise<OnboardingAdviserListResponse> {
  return apiRequest<OnboardingAdviserListResponse>({
    method: "GET",
    url: `${BASE}/onboarding/advisers`,
  });
}

export type BridgeVoiceIntakeInput = {
  mission_id?: string;
  transcript?: string;
  meeting_id?: string;
  source?: "zoom" | "upload" | "record" | "paste" | "seed";
};

export type BridgeVoiceIntakeResponse = {
  mission_id: string;
  fa_client_id?: string | null;
  task_id?: string | null;
  onboarding_intake_channel: string;
  onboarding_voice_transcript: string;
  onboarding_voice_meeting_id?: string | null;
  onboarding_voice_source: string;
};

export async function bridgeOnboardingVoiceIntake(
  body: BridgeVoiceIntakeInput,
): Promise<BridgeVoiceIntakeResponse> {
  return apiRequest<BridgeVoiceIntakeResponse>({
    method: "POST",
    url: `${BASE}/onboarding/voice-intake`,
    data: body,
  });
}

export type ContactTaskAssignee = {
  email: string;
  name?: string | null;
  user_id?: string | null;
};

export type ContactTaskDetail = {
  id: string;
  client_id: string;
  fa_client_id: string;
  mission_id: string;
  display_name: string;
  status: string;
  assignee?: ContactTaskAssignee | null;
  created_at?: string | null;
  due_at?: string | null;
  contacted_at?: string | null;
  contacted_by?: string | null;
  note?: string | null;
  crm_operation?: string | null;
  is_overdue: boolean;
  kind?: "contact" | "rebook" | string;
  lead_channel?: "cgl" | "adviser" | string | null;
  enquiry_text?: string | null;
  owner_reason?: string | null;
  opportunity_value?: string | null;
  contact_attempts?: number;
  suggested_rating?: string | null;
  suggested_rating_reason?: string | null;
  suggested_lost?: boolean;
  suggested_lost_reason?: string | null;
  reminder_raised_at?: string | null;
  reason?: string | null;
  cgl_mission_id?: string | null;
  /** The adviser's review task for this row; absent when the tenant hasn't deployed the contact review. */
  review_task_id?: string | null;
};

export type BdmTeamMember = {
  name: string;
  email: string;
  min_value: number;
  max_value?: number | null;
  label: string;
};

export async function listCglBdmTeam(): Promise<{ items: BdmTeamMember[] }> {
  return apiRequest<{ items: BdmTeamMember[] }>({
    method: "GET",
    url: `${BASE}/onboarding/cgl/bdm-team`,
  });
}

export async function recordOnboardingContactAttempt(
  taskId: string,
  body?: { note?: string },
): Promise<ContactTaskDetail> {
  return apiRequest<ContactTaskDetail>({
    method: "POST",
    url: `${BASE}/onboarding/contact-tasks/${taskId}/attempt`,
    data: body ?? {},
  });
}

export async function applyContactTaskSuggestedRating(
  taskId: string,
): Promise<ContactTaskDetail> {
  return apiRequest<ContactTaskDetail>({
    method: "POST",
    url: `${BASE}/onboarding/contact-tasks/${taskId}/apply-rating`,
    data: {},
  });
}

export async function dismissContactTaskSuggestedRating(
  taskId: string,
): Promise<ContactTaskDetail> {
  return apiRequest<ContactTaskDetail>({
    method: "POST",
    url: `${BASE}/onboarding/contact-tasks/${taskId}/dismiss-rating`,
    data: {},
  });
}

export async function confirmContactTaskSuggestedLost(
  taskId: string,
): Promise<ContactTaskDetail> {
  return apiRequest<ContactTaskDetail>({
    method: "POST",
    url: `${BASE}/onboarding/contact-tasks/${taskId}/confirm-lost`,
    data: {},
  });
}

/**
 * Keep contacting an unresponsive lead instead of marking it lost.
 *
 * @param taskId - Contact task carrying the Lost suggestion
 * @param deferUntil - Optional ISO datetime when the contact task falls due again
 */
export async function dismissContactTaskSuggestedLost(
  taskId: string,
  deferUntil?: string,
): Promise<ContactTaskDetail> {
  return apiRequest<ContactTaskDetail>({
    method: "POST",
    url: `${BASE}/onboarding/contact-tasks/${taskId}/dismiss-lost`,
    data: deferUntil ? { defer_until: deferUntil } : {},
  });
}

// ── ONB-02 CGL (BDM team only) ──────────────────────────────────────────────

export type CglAdviser = {
  adviser_id: string;
  name: string;
  email: string;
  office_slug: string;
  region: string;
  accepts_cgl: boolean;
  min_case_size: number;
  conv_rate_aum_inception: number;
  open_cases: number;
  specialisms: string[];
  active: boolean;
  pronoun?: string;
  avg_book_size?: number | null;
  conv_rate_cases_12m?: number | null;
  conv_rate_aum_12m?: number | null;
  capacity_score?: number | null;
};

export type CglAdviserSuggestion = {
  adviser: CglAdviser;
  rank: number;
  location_match: "office" | "region" | "none";
  office_name?: string | null;
  fit_score?: number | null;
  match_reasons: string[];
};

export type CglAdviserSuggestions = {
  fa_client_id: string;
  items: CglAdviserSuggestion[];
  postcode?: string | null;
  lead_office_slug?: string | null;
  lead_office_name?: string | null;
  lead_region?: string | null;
  opportunity_value?: number | null;
  fallback: boolean;
  fallback_reason?: string | null;
  criteria?: string[];
  selected_adviser_id?: string | null;
  computed_at?: string | null;
};

export type CglMeetingFormat = "in_person" | "zoom";
export type CglDiscoveryOutcome = "reached" | "no_answer" | "voicemail" | "not_interested";

export type CglCallNote = {
  markdown: string;
  ok: boolean;
  violations: string[];
  word_count: number;
  sections: string[];
  source: string;
  version: number;
};

export type CglMeetingPack = {
  status: "draft" | "sent" | "cancelled";
  subject: string;
  text: string;
  html: string;
  links: { url: string; title: string }[];
  meeting_format: CglMeetingFormat;
  meeting_at: string;
  meeting_label?: string | null;
  location_url?: string | null;
  to?: string | null;
  cc?: string | null;
  adviser_name?: string | null;
  agenda_attachment?: string | null;
  email?: { status: string; to?: string | null; provider_message_id?: string | null } | null;
};

export type CglIntroEmail = {
  subject: string;
  body: string;
  status: "draft" | "sent";
  to?: string | null;
  cc?: string | null;
};

export type CglLeadState = {
  fa_client_id: string;
  lead_channel?: string | null;
  owner?: string | null;
  current_status?: string | null;
  lead_status?: string | null;
  meeting_status?: string | null;
  initial_meeting_date?: string | null;
  cgl: {
    suggestions?: CglAdviserSuggestions;
    adviser?: Pick<CglAdviser, "adviser_id" | "name" | "email" | "office_slug" | "region"> & {
      assigned_by?: string;
      from_suggestion?: boolean;
      suggestion_rank?: number | null;
    };
    discovery?: {
      outcome?: CglDiscoveryOutcome;
      transcript?: string;
      meeting_format?: CglMeetingFormat;
      meeting_at?: string;
      office_slug?: string;
    };
    call_note?: CglCallNote;
    meeting_pack?: CglMeetingPack;
    intro_email?: CglIntroEmail;
    lost?: { reason: string; handoff: string };
  };
};

export type CglOffice = { slug: string; name: string; region: string; url: string };

const CGL = `${BASE}/cgl`;

export async function getCglLeadState(faClientId: string): Promise<CglLeadState> {
  return apiRequest<CglLeadState>({ method: "GET", url: `${CGL}/leads/${faClientId}/state` });
}

export type CglAvailabilitySlot = { start: string; end: string; status: "free" | "busy" };

const MEETING_PACK = `${BASE}/meeting-pack`;

export type MeetingPackDocument = {
  key: string;
  label: string;
  required: "mandatory" | "optional" | "conditional";
  condition?: string | null;
  owning_process?: string | null;
  needs_client_action: boolean;
  reminder_only: boolean;
  note?: string | null;
  selected: boolean;
  source?: string | null;
};

export type MeetingPackConfig = {
  bu: string;
  unknown_defaults: boolean;
  documents: MeetingPackDocument[];
};

export type MeetingPackSuggestion = {
  send: MeetingPackDocument[];
  request: { key: string; label: string; source: string }[];
  request_defined: boolean;
  request_note?: string | null;
  advice_areas: string[];
};

export type MeetingPackSelection = {
  bu: string;
  client_type: string;
  advice_areas: string[];
  fees_apply: boolean;
  db_advice: boolean;
  send: Record<string, boolean>;
  request: string[];
};

export type MeetingPackEmailState = {
  status: string;
  subject: string;
  body: string;
  to?: string | null;
  cc?: string | null;
  delivery?: string | null;
  portal_activation?: { reason: string } | null;
  routes?: Record<string, string>;
};

export type MeetingPackState = {
  fa_client_id: string;
  pack?: (MeetingPackSelection & { version: number; changed_by: string; changed_at: string; email?: MeetingPackEmailState }) | null;
};

export async function getMeetingPackConfig(bu = "abacus"): Promise<MeetingPackConfig> {
  return apiRequest<MeetingPackConfig>({
    method: "GET",
    url: `${MEETING_PACK}/config?bu=${encodeURIComponent(bu)}`,
  });
}

export async function suggestMeetingPackDocuments(
  faClientId: string,
  body: {
    client_type: string;
    advice_areas?: string[];
    fees_apply?: boolean;
    db_advice?: boolean;
    preferences?: Record<string, boolean>;
  },
): Promise<MeetingPackSuggestion> {
  return apiRequest<MeetingPackSuggestion>({
    method: "POST",
    url: `${MEETING_PACK}/leads/${faClientId}/suggest`,
    data: body,
  });
}

export async function saveMeetingPackSelection(
  faClientId: string,
  body: MeetingPackSelection,
): Promise<MeetingPackState> {
  return apiRequest<MeetingPackState>({
    method: "PUT",
    url: `${MEETING_PACK}/leads/${faClientId}/selection`,
    data: body,
  });
}

export async function getMeetingPackSelection(faClientId: string): Promise<MeetingPackState> {
  return apiRequest<MeetingPackState>({
    method: "GET",
    url: `${MEETING_PACK}/leads/${faClientId}`,
  });
}

export type MeetingPackEmailBody = {
  adviser_name?: string;
  adviser_email?: string | null;
  subject?: string;
  body?: string;
};

export async function renderMeetingPackEmail(
  faClientId: string,
  body: MeetingPackEmailBody,
): Promise<MeetingPackState> {
  return apiRequest<MeetingPackState>({
    method: "POST",
    url: `${MEETING_PACK}/leads/${faClientId}/email`,
    data: body,
  });
}

export async function sendMeetingPackEmail(
  faClientId: string,
  body: MeetingPackEmailBody,
): Promise<MeetingPackState> {
  return apiRequest<MeetingPackState>({
    method: "POST",
    url: `${MEETING_PACK}/leads/${faClientId}/email/send`,
    data: body,
  });
}

export async function approveMeetingPackSelection(
  faClientId: string,
  body: MeetingPackSelection & { task_id: string },
): Promise<MeetingPackState> {
  return apiRequest<MeetingPackState>({
    method: "POST",
    url: `${MEETING_PACK}/leads/${faClientId}/selection/approve`,
    data: body,
  });
}

export async function approveMeetingPackSend(
  faClientId: string,
  body: MeetingPackEmailBody & { task_id: string },
): Promise<MeetingPackState> {
  return apiRequest<MeetingPackState>({
    method: "POST",
    url: `${MEETING_PACK}/leads/${faClientId}/email/send-approve`,
    data: body,
  });
}

export type MeetingPackChecklistItem = {
  key: string;
  label: string;
  required: "mandatory" | "optional" | "conditional";
  owning_process?: string | null;
  chosen: boolean;
  requested: boolean;
  received: boolean;
  owning_status?: string | null;
  status: "complete" | "in_progress" | "outstanding" | string;
};

export type MeetingPackChecklist = {
  items: MeetingPackChecklistItem[];
  onboarding_complete: boolean;
  note?: string | null;
};

export async function getMeetingPackChecklist(faClientId: string): Promise<MeetingPackChecklist> {
  return apiRequest<MeetingPackChecklist>({
    method: "GET",
    url: `${MEETING_PACK}/leads/${faClientId}/checklist`,
  });
}

export async function markMeetingPackReceived(
  faClientId: string,
  docKey: string,
): Promise<MeetingPackChecklist> {
  return apiRequest<MeetingPackChecklist>({
    method: "POST",
    url: `${MEETING_PACK}/leads/${faClientId}/received`,
    data: { doc_key: docKey },
  });
}

// ── ONB-02-AB Abacus CGL (Abacus CGL / Captain, and Abacus advisers) ────────

export type AbacusAdviser = CglAdviser & {
  business_unit: string;
  on_leave_until?: string | null;
  spade_rating?: string | null;
  ace_rating?: string | null;
  avg_book_size?: number | null;
  conv_rate_cases_12m?: number | null;
  conv_rate_aum_12m?: number | null;
  capacity_score?: number | null;
};

export type AbacusAdviserSuggestion = Omit<CglAdviserSuggestion, "adviser"> & { adviser: AbacusAdviser };

export type AbacusAdviserSuggestions = Omit<CglAdviserSuggestions, "items"> & {
  items: AbacusAdviserSuggestion[];
};

/** What an adviser may see before accepting: location, case size and age only. */
export type AbacusOfferSummary = {
  location?: string | null;
  case_size?: number | null;
  case_size_label?: string | null;
  age?: number | null;
};

export type AbacusOfferStatus = "pending" | "accepted" | "rejected" | "withdrawn";
export type AbacusOfferSource = "shortlist" | "off_list" | "captain";

export type AbacusOffer = {
  id: string;
  fa_client_id: string;
  mission_id?: string | null;
  adviser_id: string;
  adviser_name: string;
  adviser_email: string;
  rank?: number | null;
  source: AbacusOfferSource;
  status: AbacusOfferStatus;
  summary: AbacusOfferSummary;
  reason?: string | null;
  created_at?: string | null;
  decided_at?: string | null;
  decided_by?: string | null;
};

export type AbacusLeadState = {
  fa_client_id: string;
  business_unit: string;
  mission_id?: string | null;
  suggestions?: AbacusAdviserSuggestions | null;
  queue: string[];
  offers: AbacusOffer[];
  current_offer?: AbacusOffer | null;
  outcome?: "offered" | "accepted" | "exhausted" | null;
  captain_required: boolean;
  selected_by?: { email?: string; name?: string; at?: string } | null;
  accepted_adviser?: { adviser_id: string; name?: string; email?: string; source?: string } | null;
  curo_adviser_sync?: { status?: string; curo_ref?: string; synced_at?: string } | null;
  summary: AbacusOfferSummary;
};

const ABACUS = `${BASE}/abacus-cgl`;

export async function listMyAbacusOffers(
  status: AbacusOfferStatus | "all" = "pending",
): Promise<{ items: AbacusOffer[] }> {
  return apiRequest<{ items: AbacusOffer[] }>({ method: "GET", url: `${ABACUS}/offers`, params: { status } });
}

export type AbacusOfferDecision = { offer_id: string; status: "accepted" | "rejected"; fa_client_id?: string | null };

export async function acceptAbacusOffer(offerId: string): Promise<AbacusOfferDecision> {
  return apiRequest<AbacusOfferDecision>({ method: "POST", url: `${ABACUS}/offers/${offerId}/accept`, data: {} });
}

export async function rejectAbacusOffer(offerId: string, reason?: string | null): Promise<AbacusOfferDecision> {
  return apiRequest<AbacusOfferDecision>({
    method: "POST",
    url: `${ABACUS}/offers/${offerId}/reject`,
    data: { reason: reason?.trim() || null },
  });
}

const BOOKING = `${BASE}/booking`;

export type LeadEmailResult = {
  to: string;
  status: string;
  provider_message_id?: string | null;
  subject?: string | null;
};

export async function sendLeadEmail(
  faClientId: string,
  body: { subject: string; message: string },
): Promise<LeadEmailResult> {
  return apiRequest<LeadEmailResult>({
    method: "POST",
    url: `${BOOKING}/leads/${faClientId}/send-email`,
    data: body,
  });
}

/** ONB-13 record lost lead (reason catalogue, record-loss commit, permission, follow-up, reinstate). */
export type LostReason = {
  name: string;
  attribution: string;
  status: string;
  date_added?: string | null;
  date_retired?: string | null;
};

export type LostReasonList = {
  items: LostReason[];
  total: number;
};

export type RecordLostResult = {
  fa_client_id: string;
  lead_status: string;
  reason: string;
  milestones?: string[];
  contact_permission: string;
  closed_contact_tasks?: number;
  closed_direct_tasks?: number;
  archived_missions?: number;
  cancelled_meeting?: boolean;
};

/** Monthly ONB-13 loss figures; each `by_*` map is label → count. */
export type LossReport = {
  period: string;
  total_losses: number;
  by_reason: Record<string, number>;
  by_source: Record<string, number>;
  by_record_state: Record<string, number>;
  by_owner: Record<string, number>;
  by_business_unit: Record<string, number>;
  by_attribution: Record<string, number>;
  converted: number;
  open: number;
  adviser_conversion_rate: number | null;
};

export type LostLeadSummary = {
  fa_client_id: string;
  lead_status: string | null;
  contact_permission: string | null;
  followup_date: string | null;
};

const LOST = `${BASE}/onboarding`;

/** Active lost reasons for the record-loss dropdown. Seeds once on first read. */
export async function getLostReasons(): Promise<LostReasonList> {
  return apiRequest<LostReasonList>({ method: "GET", url: `${LOST}/lost-reasons` });
}

export async function recordLostLead(
  faClientId: string,
  body: { reason: string; detail: string; record_state?: string; recorded_via?: string },
): Promise<RecordLostResult> {
  return apiRequest<RecordLostResult>({
    method: "POST",
    url: `${LOST}/leads/${faClientId}/record-lost`,
    data: body,
  });
}

/**
 * Loss figures for one month.
 *
 * @param period - Month as `YYYY-MM`
 * @param scope - `all` for the firm, `me` for the caller's own leads
 */
export async function getLossReport(period: string, scope: "all" | "me" = "all"): Promise<LossReport> {
  return apiRequest<LossReport>({ method: "GET", url: `${LOST}/loss-report`, params: { period, scope } });
}

export async function getLeadCallLink(
  faClientId: string,
): Promise<{ join_url: string | null }> {
  return apiRequest<{ join_url: string | null }>({
    method: "GET",
    url: `${BOOKING}/leads/${faClientId}/call-link`,
  });
}

export async function getLeadEmailStatus(
  faClientId: string,
): Promise<{ to: string | null; can_send: boolean }> {
  return apiRequest<{ to: string | null; can_send: boolean }>({
    method: "GET",
    url: `${BOOKING}/leads/${faClientId}/email-status`,
  });
}

export type SaltusAskAniMessage = {
  id: string;
  role: string;
  text: string;
  created_at?: string | null;
};

export async function listSaltusAskAniChat(
  taskId: string,
): Promise<SaltusAskAniMessage[]> {
  return apiRequest<SaltusAskAniMessage[]>({
    method: "GET",
    url: `${BOOKING}/review-tasks/${taskId}/ask-ani/chat`,
  });
}

export async function startNewSaltusAskAniChat(
  taskId: string,
): Promise<{ thread_id: string }> {
  return apiRequest<{ thread_id: string }>({
    method: "POST",
    url: `${BOOKING}/review-tasks/${taskId}/ask-ani/chat/new`,
  });
}

export async function askSaltusAni(
  taskId: string,
  body: { text: string },
): Promise<SaltusAskAniMessage> {
  return apiRequest<SaltusAskAniMessage>({
    method: "POST",
    url: `${BOOKING}/review-tasks/${taskId}/ask-ani/ask`,
    data: body,
  });
}

export async function setLostContactPermission(
  faClientId: string,
  body: { permission: string; change_reason?: string },
): Promise<LostLeadSummary> {
  return apiRequest<LostLeadSummary>({
    method: "PATCH",
    url: `${LOST}/leads/${faClientId}/contact-permission`,
    data: body,
  });
}

export async function setLostFollowupDate(
  faClientId: string,
  body: { date: string; reason: string },
): Promise<LostLeadSummary> {
  return apiRequest<LostLeadSummary>({
    method: "PUT",
    url: `${LOST}/leads/${faClientId}/follow-up`,
    data: body,
  });
}

export async function reinstateLostRecord(
  faClientId: string,
  body?: { reason?: string },
): Promise<LostLeadSummary> {
  return apiRequest<LostLeadSummary>({
    method: "POST",
    url: `${LOST}/leads/${faClientId}/reinstate`,
    data: body ?? {},
  });
}

export type ContactTaskListResponse = {
  items: ContactTaskDetail[];
  total: number;
};

export async function listOnboardingContactTasks(params?: {
  scope?: "me" | "team";
  status?: "open" | "overdue" | "contacted" | "all";
  limit?: number;
}): Promise<ContactTaskListResponse> {
  return apiRequest<ContactTaskListResponse>({
    method: "GET",
    url: `${BASE}/onboarding/contact-tasks`,
    params,
  });
}

export async function assignOnboardingContactTask(
  taskId: string,
  body: { email: string },
): Promise<ContactTaskDetail> {
  return apiRequest<ContactTaskDetail>({
    method: "POST",
    url: `${BASE}/onboarding/contact-tasks/${taskId}/assign`,
    data: body,
  });
}

export async function completeOnboardingContactTask(
  taskId: string,
  body?: { note?: string },
): Promise<ContactTaskDetail> {
  return apiRequest<ContactTaskDetail>({
    method: "POST",
    url: `${BASE}/onboarding/contact-tasks/${taskId}/complete`,
    data: body ?? {},
  });
}

export type CompanyDemoPackItem = {
  id: string;
  kind: "scenario" | "action";
  label: string;
  description: string;
  section: string;
  requirement_code?: string | null;
};

export type CompanyDemoPackActive = {
  pack_id: string;
  title: string;
  client_id: string;
  advisor_surface_id: string;
  cleanup_hint?: string;
  items: CompanyDemoPackItem[];
  journeys: CompanyDemoJourney[];
};

export type CompanyDemoJourney = {
  id: string;
  title: string;
  description: string;
  modes: DemoJourneyMode[];
  /** Lower-case extensions this journey starts from when dropped onto Ani. Empty: it starts from the Demo Bar. */
  accepts_files: string[];
  /** What Ani tells the presenter once the journey is selected. */
  start_hint: string;
  sample_file_name: string | null;
  requirement_code?: string;
  menu_label?: string;
  menu_summary?: string;
};

export async function getActiveCompanyDemoPack(): Promise<CompanyDemoPackActive> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/demo/packs/active`,
  });
}

const DEMO_SEED_POLL_MS = 1_500;
const DEMO_SEED_MAX_WAIT_MS = 300_000;

type DemoSeedJob = { status: "running" | "done" | "failed"; result?: Record<string, unknown> | null; error?: string | null };

/** Seeding runs detached server-side (longer than the request timeout); resolves once the job settles. */
export async function seedActiveCompanyDemoPackItem(itemId: string): Promise<Record<string, unknown>> {
  const { job_id: jobId } = await apiRequest<{ job_id: string }>({ method: "POST", url: `${BASE}/demo/packs/active/seed`, data: { item_id: itemId } });
  const startedAt = Date.now();
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, DEMO_SEED_POLL_MS));
    const job = await apiRequest<DemoSeedJob>({ method: "GET", url: `${BASE}/demo/packs/active/seed/${encodeURIComponent(jobId)}` });
    if (job.status === "done") return job.result ?? {};
    if (job.status === "failed") throw new Error(job.error ?? "Demo seed failed");
    if (Date.now() - startedAt > DEMO_SEED_MAX_WAIT_MS) throw new Error("Demo seed is taking too long — check the worker");
  }
}

export async function getSeededDemoTaskPreparing(taskId: string): Promise<{ preparing: boolean }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/demo/packs/active/tasks/${encodeURIComponent(taskId)}/preparing`,
  });
}

export async function cleanupActiveCompanyDemoPack(): Promise<Record<string, unknown>> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/demo/packs/active/cleanup`,
    timeout: 120_000,
  });
}

export type DemoJourneyMode = "scripted" | "live";

export type DemoJourneyEvent = {
  id: string;
  key: string;
  at: string;
  title: string;
  body: string;
  /** Ani's line, as sanitised-chat HTML. */
  ani: string;
  recap: string;
  open: FeToolCall | null;
  simulate: { action: string; label: string } | null;
};

export type DemoJourneyState = {
  active: boolean;
  journey_id: string | null;
  mode: DemoJourneyMode | null;
  stage: string | null;
  events: DemoJourneyEvent[];
};

const DEMO_JOURNEY_URL = `${BASE}/demo/packs/active/journey`;
const DEMO_JOURNEYS_URL = `${BASE}/demo/packs/active/journeys`;

export async function getDemoJourney(): Promise<DemoJourneyState> {
  return apiRequest({ method: "GET", url: DEMO_JOURNEY_URL });
}

/**
 * Reset and start a guided journey, optionally from a dropped file.
 * Ani's lines are saved into the given Ani chat session so they survive a reload.
 */
export async function startDemoJourney(
  journeyId: string,
  {
    mode,
    chatSessionId,
    file,
    uploadMessageId,
  }: { mode: DemoJourneyMode; chatSessionId: string; file?: File; uploadMessageId?: string },
): Promise<DemoJourneyState> {
  const formData = new FormData();
  if (file) formData.append("file", file);
  if (uploadMessageId) formData.append("upload_message_id", uploadMessageId);
  formData.append("mode", mode);
  formData.append("chat_session_id", chatSessionId);
  return apiRequest({ method: "POST", url: `${DEMO_JOURNEYS_URL}/${journeyId}/start`, data: formData, timeout: 120_000 });
}

export async function syncDemoJourney(): Promise<DemoJourneyState> {
  return apiRequest({ method: "POST", url: `${DEMO_JOURNEY_URL}/sync`, timeout: 120_000 });
}

export async function simulateDemoJourney(action: string): Promise<DemoJourneyState> {
  return apiRequest({ method: "POST", url: `${DEMO_JOURNEY_URL}/simulate/${action}`, timeout: 120_000 });
}

export async function resetDemoJourney(journeyId: string): Promise<DemoJourneyState> {
  return apiRequest({ method: "POST", url: `${DEMO_JOURNEYS_URL}/${journeyId}/reset`, timeout: 120_000 });
}

export function demoJourneySampleFileUrl(journeyId: string): string {
  return `${DEMO_JOURNEYS_URL}/${journeyId}/sample-file`;
}

/** Same sample file path, prefixed for ``<a href download>`` (axios paths are not app routes). */
export function demoJourneySampleFileDownloadHref(journeyId: string): string {
  return browserApiUrl(demoJourneySampleFileUrl(journeyId));
}

export async function fetchDemoJourneySampleFile(journeyId: string): Promise<Blob> {
  return apiRequest<Blob>({ method: "GET", url: demoJourneySampleFileUrl(journeyId), responseType: "blob" });
}

export type SaltusOnboardingDemoScenario = {
  id: string;
  label: string;
  description: string;
  gate: string;
};

export async function listSaltusOnboardingDemoScenarios(): Promise<{
  scenarios: SaltusOnboardingDemoScenario[];
}> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/demo/saltus-onboarding/scenarios`,
  });
}

export async function seedSaltusOnboardingDemo(scenario: string): Promise<{
  scenario: string;
  mission_id: string;
  fa_client_id: string;
  task_id: string | null;
  gate: string;
  lead_name: string;
}> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/demo/saltus-onboarding/seed`,
    data: { scenario },
  });
}

/** Demo: generate today's AI standup from the adviser's live tasks and meetings. */
export async function seedSaltusStandupDemo(): Promise<StandupBriefing> {
  return apiRequest<StandupBriefing>({
    method: "POST",
    url: `${BASE}/demo/saltus-onboarding/standup`,
    timeout: 90_000,
  });
}

export async function seedSaltusMeetingsDemo(): Promise<{
  meeting_id: string;
  mission_id: string;
  fa_client_id: string;
  client_name: string;
  skipped?: boolean;
}> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/demo/saltus-onboarding/seed-meetings`,
    timeout: 60_000,
  });
}

export async function cleanupSaltusOnboardingDemo(): Promise<{
  ok: boolean;
  remaining_demo_leads: number;
}> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/demo/saltus-onboarding/cleanup`,
  });
}

export type {
  AddArtifactCommentInput,
  AgentSummary,
  ApplyTaskActionRequest,
  Artifact,
  ArtifactApproval,
  ArtifactAzureStorage,
  ArtifactChatMessage,
  ArtifactComment,
  ArtifactCommentAnchor,
  ArtifactCommentAuthor,
  ArtifactCommentStatus,
  AskAniChatAction,
  AskAniIntent,
  ArtifactDocument,
  ArtifactDocumentJoined,
  ArtifactDocumentVersion,
  ArtifactGeneratedBy,
  ArtifactKind,
  ArtifactPlannrStorage,
  ArtifactReportBuilderStorage,
  ArtifactStatus,
  ArtifactStorage,
  ClientMemory,
  ClientTimelineEntry,
  CreateClientMemoryRequest,
  CreateCaseCheckMissionRequest,
  CreateCaseCheckMissionResponse,
  CreateDisclosureMissionRequest,
  CreateDisclosureMissionResponse,
  CreateLoaInput,
  CreateMissionRequest,
  CreateMissionResponse,
  CrmField,
  DocumentFormSchema,
  DocumentTemplate,
  FormField,
  FormFieldOption,
  FormSchema,
  FormFieldType,
  ClientHealthcheck,
  ClientInsight,
  PlannrTaskAnalysis,
  FaClientAiInsightStub,
  FaClientCrmSignals,
  FaClientDetail,
  FaClientLeadBlock,
  FaClientListItem,
  FaClientMember,
  FaClientSummary,
  FaClientUpdate,
  FaLeadListItem,
  FaLeadStatuses,
  GateBlockedDisplay,
  GateBlockedReason,
  ListArtifactsParams,
  ListFaClientsParams,
  ListLeadsParams,
  ListMissionsParams,
  MissionAttention,
  MissionContextData,
  MissionDetail,
  MissionEntityRef,
  MissionLane,
  MissionMatchCandidate,
  MissionStage,
  MissionSummary,
  MissionTaskGraphNode,
  MissionTaskSummary,
  MissionTemplateSummary,
  CodexMissionBlueprintNode,
  CodexMissionTemplateDetail,
  CellOrigin,
  ResourceLinkCell,
  ResourceLinkType,
  TableColumn,
  TableColumnOption,
  TableColumnType,
  TableRow,
  TableRowSourceFile,
  TableSchema,
  TaskActionItem,
  TaskActionSpec,
  TaskAgentRef,
  TaskBucket,
  TaskCatalogueItem,
  TaskDetail,
  TaskEmbedParams,
  TaskEntityRef,
  TaskMissionRef,
  ThreadEntry,
  ThreadMessage,
  ThreadInteraction,
  ThreadView,
  InteractionStep,
  MessageAttachment,
  PostMessageRequest,
  UploadDocumentResponse,
  PlannrFormSubmissionResponse,
  PlannrFormsListResponse,
  PlannrFormSummary,
  PlannrWebhookRun,
  PlannrWebhookRunDispatchedSummary,
  TriggerEventRow,
  TriggerEventRuleOutcome,
  TriggerPendingAction,
  TriggerRule,
  TriggerRuleOutcome,
  VisibilityScope,
  CreateFactfindResult,
  FactfindDefaultsRule,
  FactfindPortalEventKind,
  FactfindRead,
  OutstandingFactfind,
} from "./types";

export {
  brandingKeys,
  getResolvedBranding,
  getCompanyBrandingAdmin,
  putCompanyBrandingAdmin,
  createBusinessUnit,
  updateBusinessUnit,
  deleteBusinessUnit,
  uploadBrandingLogo,
  deleteBrandingLogo,
  uploadBrandingBackgroundImage,
  deleteBrandingBackgroundImage,
  uploadBrandingWatermarkLogo,
  deleteBrandingWatermarkLogo,
} from "./branding";
export type {
  BrandingConfig,
  BrandingPolicy,
  BrandingScope,
  BrandingUploadResult,
  BusinessUnit,
  CompanyBrandingAdmin,
  EffectiveBranding,
} from "./types";

/* ONB-05 Factfind read, outstanding, fields, submit, reminders, defaults, demo events. */

export async function getFactfindRead(faClientId: string): Promise<FactfindRead> {
  return apiRequest<FactfindRead>({
    method: "GET",
    url: `${BASE}/onboarding/leads/${faClientId}/factfind`,
  });
}

export async function listOutstandingFactfinds(
  scope: "me" | "team" = "team",
): Promise<{ items: OutstandingFactfind[]; total: number }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/onboarding/factfinds/outstanding`,
    params: { scope },
  });
}

export async function updateFactfindFields(
  faClientId: string,
  fields: Record<string, unknown>,
  source: "adviser" | "meeting_intelligence" = "adviser",
): Promise<Record<string, unknown>> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/onboarding/factfind/fields`,
    data: { fa_client_id: faClientId, fields, source },
  });
}

export async function populateFactfindFromMi(
  faClientId: string,
  fieldNames: string[] = [],
): Promise<Record<string, unknown>> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/onboarding/factfind/populate-from-mi`,
    data: { fa_client_id: faClientId, fields: Object.fromEntries(fieldNames.map((k) => [k, true])) },
  });
}

export async function submitFactfind(faClientId: string): Promise<Record<string, unknown>> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/onboarding/factfind/submit`,
    data: { fa_client_id: faClientId },
  });
}

export async function remindFactfind(faClientId: string): Promise<Record<string, unknown>> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/onboarding/factfind/remind`,
    data: { fa_client_id: faClientId },
  });
}

export async function getFactfindDefaults(): Promise<{ rules: FactfindDefaultsRule[] }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/onboarding/factfind/defaults`,
  });
}

export async function putFactfindDefaults(
  rules: FactfindDefaultsRule[],
): Promise<{ rules: FactfindDefaultsRule[] }> {
  return apiRequest({
    method: "PUT",
    url: `${BASE}/onboarding/factfind/defaults`,
    data: { rules },
  });
}

export async function sendFactfindEvent(
  faClientId: string,
  event: FactfindPortalEventKind,
): Promise<Record<string, unknown>> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/demo/saltus-onboarding/leads/${faClientId}/factfind-event`,
    data: { event },
    timeout: 120_000,
  });
}

/** ONB-05 ad-hoc start: create the Factfind record on a lead from the client record. */
export async function createFactfind(faClientId: string): Promise<CreateFactfindResult> {
  return apiRequest<CreateFactfindResult>({
    method: "POST",
    url: `${BASE}/onboarding/create-factfind`,
    data: { fa_client_id: faClientId },
  });
}
